/**
 * Entity → tessellated-polyline conversion and the unified curve×curve
 * intersection dispatcher (`entityIntersectionPoints`) — split out of
 * `trimExtendGeometry.ts` (Codex round-15 static-analysis gate: that file
 * exceeded the project's 2000-line hard ceiling after 15 rounds of local
 * Codex review hardening). Pure structural extraction, no behaviour change
 * — `trimExtendGeometry.ts` re-exports everything here so every external
 * import path (`from "./trimExtendGeometry"`, and the package barrel
 * `@dxf-ai-editor/command-engine`) keeps working unchanged.
 *
 * Covers: entity tessellation (`entityToPolyline` and its LWPOLYLINE/Spline
 * helpers), the closed-form analytic dispatch (`closedFormIntersection`,
 * `asCircle`), the tessellation fallback (`polyPolyIntersection` and the
 * infinite-carrier constructors), and the analytic tangent-detection
 * supplement (`bulgeSegmentAnalyticHits` and friends) that rounds 8-15
 * progressively hardened. See `trimExtendGeometry.ts`'s own module doc
 * comment for the feature-level (AutoCAD parity) documentation this file
 * shares.
 */

import type { DrawingEntity, EllipseEntity, SplineNurbsResolvable } from "@dxf-ai-editor/drawing-model";
import {
  bulgeArc,
  bulgeArcPointAt,
  evaluateNurbs,
  nurbsKnotSpanBreakpoints,
  resolveSplineNurbs,
  type BulgeArc,
} from "@dxf-ai-editor/drawing-model";
import {
  lineArcIntersection,
  lineCircleIntersection,
  lineLineIntersection,
} from "./geometryTransforms";
import { heavyPolylineChain } from "./heavyPolylineTrim";

import {
  angleDegOf,
  angleInArcDeg,
  bulgeArcToGeom,
  circleCircleIntersection,
  CURVE_TESSELLATION_SAMPLES,
  ellipsePointAtParam,
  GEO_EPS,
  lineEllipseIntersection,
  MAX_TOTAL_SPLINE_TESSELLATION_SAMPLES,
  polylineCarrierRadialTolerance,
  sweepDeg,
  sweepRad,
  type CarrierTaggedPoint,
  type IntPoint,
} from "./trimExtendGeometryCore";
import {
  extentMargin,
  infiniteLineSegment,
  polylineInfiniteCarrier,
  polylineInfiniteCarrierParts,
  splineEndStretch,
  stretchPolylineEndsToInfinite,
} from "./trimExtendInfiniteCarrier";

// ── Segment intersection (tessellation fallback) ───────────────────

/** Intersection of two finite segments, or null if they do not cross. */
function segSegIntersection(
  a: IntPoint,
  b: IntPoint,
  c: IntPoint,
  d: IntPoint,
): IntPoint | null {
  const r = lineLineIntersection(a, b, c, d);
  if (!r) return null;
  const onSeg = (p: IntPoint, s: IntPoint, e: IntPoint): boolean => {
    const len2 = (e.x - s.x) ** 2 + (e.y - s.y) ** 2;
    if (len2 < GEO_EPS) return false;
    const t = ((p.x - s.x) * (e.x - s.x) + (p.y - s.y) * (e.y - s.y)) / len2;
    // BEST-EFFORT-EXHAUSTED BOUNDARY (mutation-testing skill's convention —
    // NOT claimed as an equivalence proof) for the `t <= 1 + 1e-6` ->
    // `t < 1 + 1e-6` EqualityOperator mutant specifically (the mirrored
    // `t >= -1e-6` lower-bound boundary WAS achieved — see the mutation
    // test suite's dedicated case): `t` here is derived from `r`
    // (`lineLineIntersection`'s own division-heavy output), then
    // re-projected through a SECOND division by `len2` — the two chained
    // divisions consistently rounded the boundary construction attempted
    // (`qx` chosen to land `r.x` at exactly `(1+1e-6)*10`) to ONE ULP
    // ABOVE the literal `1 + 1e-6`, regardless of which nearby `qx`
    // candidate was tried. Near-boundary coverage on both sides exists
    // (comfortably within vs. comfortably outside the tolerance).
    // Stryker disable next-line EqualityOperator: exact t===1+1e-6 bit boundary resisted construction through the two chained divisions (lineLineIntersection's own t, then this re-projection) — see best-effort-exhausted note above; near-boundary coverage on both sides exists
    return t >= -1e-6 && t <= 1 + 1e-6;
  };
  return onSeg(r, a, b) && onSeg(r, c, d) ? r : null;
}

/**
 * The `{x, y, bulge?}` chain a POLYLINE-FAMILY entity presents to every helper
 * in this module that reasons about polyline SEGMENTS — the infinite EXTEND
 * carrier (`polylineInfiniteCarrier(Parts)`) and the two analytic
 * tangency/secant supplements (`bulgeSegmentAnalyticHits`,
 * `polylineEdgeAnalyticHits`) — or `null` when `entity` is not one.
 *
 * ONE truth for the two families so both behave identically (@codex PR #785
 * round 4, P2): a lightweight polyline hands over its own vertex array; a heavy
 * POLYLINE hands over its DRAWN chain, resolved by `cad-core`'s topology
 * through {@link heavyPolylineChain} — which also declines the mesh subtypes,
 * whose MANY runs (rows / columns / face edges) are not one chain at all.
 * Without this, every supplement silently skipped a heavy polyline and its
 * bulged spans were intersected on the tessellated CHORD alone.
 */
function polylineFamilyChain(
  entity: DrawingEntity,
): { vertices: Array<{ x: number; y: number; bulge?: number }>; closed: boolean } | null {
  if (entity.type === "LWPOLYLINE") {
    return { vertices: entity.vertices, closed: entity.closed };
  }
  if (entity.type === "Polyline") {
    // EQUIVALENT MUTANT (targeted-cad-mutation-testing's equivalent-mutant
    // contract), StringLiteral `"edge"` -> `""`: `heavyPolylineTrimRefusal`
    // branches on `role === "target"` ALONE, and everything outside that branch
    // is the role-blind mesh test — so every non-`"target"` string resolves the
    // identical chain. `HeavyPolylineTrimRole` makes `""` unreachable outside a
    // mutant. (Proven by `heavyPolylineTrim.test.ts`'s role matrix, which pins
    // what each role does decide.)
    // Stryker disable next-line StringLiteral: the predicate branches on `role === "target"` only, so every non-"target" string behaves identically to "edge" (see equivalent-mutant note above)
    const chain = heavyPolylineChain(entity, "edge");
    return chain
      ? { vertices: chain.vertices.map((v) => ({ ...v })), closed: chain.closed }
      : null;
  }
  return null;
}

/**
 * The chain an OPEN polyline-family TARGET presents to the infinite EXTEND
 * carrier, or `null` when there is no dangling end to grow (a CLOSED polyline
 * of either family) or `target` is not a polyline at all — both of which fall
 * through to the plain real-extent tessellation.
 */
function openPolylineCarrierChain(
  target: DrawingEntity,
): Array<{ x: number; y: number; bulge?: number }> | null {
  const chain = polylineFamilyChain(target);
  return chain && !chain.closed ? chain.vertices : null;
}

/** Intersect a polyline (as segment list) against another polyline. */
function polyPolyIntersection(p: IntPoint[], q: IntPoint[]): IntPoint[] {
  const out: IntPoint[] = [];
  for (let i = 0; i + 1 < p.length; i++) {
    for (let j = 0; j + 1 < q.length; j++) {
      const hit = segSegIntersection(p[i]!, p[i + 1]!, q[j]!, q[j + 1]!);
      if (hit) out.push(hit);
    }
  }
  return out;
}

// ── Entity → primitive-curve tessellation ──────────────────────────

/**
 * Sample any drawing entity into a dense polyline for the tessellation
 * intersection fallback. Returns `null` for a type that carries no linear
 * geometry (Text / Block / Dimension …), which then contributes no cut points.
 */
export function entityToPolyline(entity: DrawingEntity): IntPoint[] | null {
  switch (entity.type) {
    case "Line":
      return [entity.start, entity.end];
    case "Circle": {
      const pts: IntPoint[] = [];
      for (let i = 0; i <= CURVE_TESSELLATION_SAMPLES; i++) {
        const t = (i / CURVE_TESSELLATION_SAMPLES) * 2 * Math.PI;
        pts.push({
          x: entity.center.x + entity.radius * Math.cos(t),
          y: entity.center.y + entity.radius * Math.sin(t),
        });
      }
      return pts;
    }
    case "Arc": {
      // MUTATION-TESTING NOTE (RuntimeError classification, not Survived —
      // documented per the targeted-cad-mutation-testing skill's rigor,
      // though the skill's Verdict policy only names Killed/Timeout/
      // Survived/NoCoverage explicitly): the `total / 360` -> `total * 360`
      // ArithmeticOperator mutant here is reported by Stryker as
      // `RuntimeError`, not `Survived` — applying it makes `n` explode to
      // `Math.ceil(total * 360 / 360 * 720)` = `Math.ceil(total * 720)`,
      // i.e. tens-of-millions for any ordinary `total`, and the `for (i=0;
      // i<=n; i++)` loop below then attempts to allocate/push an
      // array of that size, which the mutation-testing sandbox's own test
      // runner reports as a runtime failure (OOM/array-length) before any
      // `expect()` in this suite even runs. `total` is contractually
      // bounded to `(0, 360]` by `sweepDeg`'s own documented invariant
      // (`trimExtendGeometryCore.ts`) for EVERY non-mutated call site —
      // this mutant's catastrophic blow-up is therefore purely a
      // mutation-testing artifact of deliberately corrupting that bound,
      // not a reachable production robustness gap, and Stryker's own
      // default scoring already excludes `RuntimeError` mutants from both
      // the killed and survived buckets (this sub-batch's 99.42% score is
      // exactly `(killed + timeout) / (killed + timeout + survived)`,
      // confirming the single RuntimeError mutant here is excluded from
      // the denominator) — matching this same gate's established Timeout-
      // mutant convention (batches 1-4 of this file's own mutation series)
      // rather than requiring a test that would itself need to survive an
      // intentional out-of-memory allocation to observe the difference.
      const total = sweepDeg(entity.startAngle, entity.endAngle);
      const n = Math.max(2, Math.ceil((total / 360) * CURVE_TESSELLATION_SAMPLES));
      const pts: IntPoint[] = [];
      for (let i = 0; i <= n; i++) {
        const deg = entity.startAngle + (total * i) / n;
        const rad = (deg * Math.PI) / 180;
        pts.push({
          x: entity.center.x + entity.radius * Math.cos(rad),
          y: entity.center.y + entity.radius * Math.sin(rad),
        });
      }
      return pts;
    }
    case "Ellipse": {
      const total = sweepRad(entity.startParam, entity.endParam);
      const n = Math.max(2, Math.ceil((total / (2 * Math.PI)) * CURVE_TESSELLATION_SAMPLES));
      const pts: IntPoint[] = [];
      for (let i = 0; i <= n; i++) {
        pts.push(ellipsePointAtParam(entity, entity.startParam + (total * i) / n));
      }
      return pts;
    }
    case "LWPOLYLINE":
    case "Revision Cloud": {
      const pts = polylineToPoints(entity);
      return pts.length >= 2 ? pts : null;
    }
    case "Polyline": {
      // Heavy POLYLINE as a cutting / boundary EDGE (@codex PR #785 round 4,
      // P2). The DRAWN 2D chain — which records are visible, in what order,
      // which spans are bulged — comes from `cad-core`'s one-truth topology
      // (`heavyPolylineChain` -> `heavyPolylineRuns`), never re-decoded here,
      // and is then handed to the SAME `polylineToPoints` tessellator the
      // LWPOLYLINE arm uses, so a bulged span is sampled on its ARC by the
      // same one-truth `bulgeArc` conversion instead of a fresh copy.
      //
      // `heavyPolylineChain` returns `null` for a polygon / poly-face mesh:
      // Autodesk's TRIM/EXTEND cutting-edge list does not include meshes, and
      // a mesh has no single 2D path at all (`heavyPolylineRuns` yields MANY
      // runs — rows, columns, face edges). A 2D or 3D polyline, curve- or
      // spline-fitted or not, IS supported here: an edge only contributes
      // crossing points, so the XY projection AutoCAD's own default
      // `PROJMODE = 1` uses is exact for it.
      // EQUIVALENT MUTANT, StringLiteral `"edge"` -> `""`: same proof as
      // `polylineFamilyChain`'s own call above.
      // Stryker disable next-line StringLiteral: the predicate branches on `role === "target"` only (see equivalent-mutant note on polylineFamilyChain)
      const chain = heavyPolylineChain(entity, "edge");
      if (!chain) return null;
      const pts = polylineToPoints({
        vertices: chain.vertices.map((v) => ({ x: v.x, y: v.y, bulge: v.bulge })),
        closed: chain.closed,
      });
      // EQUIVALENT MUTANT, ConditionalExpression `pts.length >= 2` -> `true`:
      // unlike the LWPOLYLINE arm above — where a 1-vertex polyline reaches
      // this check and the guard IS killable — a heavy chain can never be short
      // enough. `heavyPolylineChain` returns `null` below two indices, and
      // `polylineToPoints` emits `1 + segCount` points with `segCount >= 1`, so
      // `pts.length` here is ALWAYS `>= 2`. The guard is kept for symmetry with
      // its sibling arm, not as reachable logic.
      // Stryker disable next-line ConditionalExpression: heavyPolylineChain's own >=2-index floor makes polylineToPoints emit >=2 points for every heavy polyline reaching this line (see equivalent-mutant note above)
      return pts.length >= 2 ? pts : null;
    }
    case "Spline": {
      const pts = splineToPoints(entity);
      // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
      // contract) for the `>= 2` -> `> 2` EqualityOperator mutant
      // specifically (the `>=2` boundary IS independently killed for the
      // LWPOLYLINE case above, which can legitimately return EXACTLY 2
      // points): `splineToPoints`'s successful (resolved) path can NEVER
      // return exactly 2 points. Every span's own sample count has a hard
      // floor of `minSamplesPerSpan - 1`, and `minSamplesPerSpan` is
      // itself always `>= Math.max(2, Math.min(idealMinSamplesPerSpan,
      // ...))` — for the simplest possible curve (a single span),
      // `idealMinSamplesPerSpan = Math.max(degree+1, 8) >= 8` and
      // `Math.floor(MAX_TOTAL_SPLINE_TESSELLATION_SAMPLES / 1)` is huge,
      // so `minSamplesPerSpan` is always `8`, giving `us.length >= 1 + 7 =
      // 8` for ANY successful resolve. The ONLY other path (`!resolved` /
      // the `catch` fallback) returns `entity.fitPoints` directly — and
      // `resolved` can only be `null` when `fitPoints.length < 2` (see the
      // proof on that guard's own line), so THAT path can only ever
      // return 0 or 1 points. `pts.length` is therefore ALWAYS either
      // `<= 1` or `>= 8` here — never exactly 2 — making `>` and `>=`
      // provably indistinguishable at this specific site.
      // Stryker disable next-line EqualityOperator: splineToPoints can never return exactly 2 points (structural floor: successful resolve always gives >=8, the only failure path gives <=1) — see equivalent-mutant note above
      return pts.length >= 2 ? pts : null;
    }
    default:
      return null;
  }
}

/**
 * Decompose an LWPOLYLINE / Revision Cloud into a dense point list, tessellating
 * bulge (arc) segments so curved edges intersect correctly.
 */
function polylineToPoints(entity: {
  vertices: Array<{ x: number; y: number; bulge?: number }>;
  closed: boolean;
}): IntPoint[] {
  const verts = entity.vertices;
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract) for the `[]` -> `["Stryker was here"]` ArrayDeclaration
  // mutant: this function's ONLY caller (`entityToPolyline`'s LWPOLYLINE
  // case) rejects ANY result shorter than 2 points regardless of its
  // CONTENTS (`pts.length >= 2 ? pts : null`) — a length-0 array and a
  // length-1 poisoned array both fail that check identically, giving the
  // SAME final `null`. No test can observe the difference.
  // Stryker disable next-line ArrayDeclaration: the only caller rejects any result shorter than 2 points regardless of contents — a 0-length and a 1-length poisoned array are both rejected identically (see equivalent-mutant note above)
  if (verts.length === 0) return [];
  const pts: IntPoint[] = [{ x: verts[0]!.x, y: verts[0]!.y }];
  const segCount = entity.closed ? verts.length : verts.length - 1;
  for (let i = 0; i < segCount; i++) {
    const v0 = verts[i]!;
    const v1 = verts[(i + 1) % verts.length]!;
    const bulge = v0.bulge ?? 0;
    // One-truth bulge → arc geometry. Re-deriving the (sign-sensitive) bulge
    // trigonometry locally previously sampled the arc on the WRONG side of the
    // chord, so tessellated intersections against bulged edges were found in
    // the wrong place. `bulgeArc` is the SINGLE source of truth (drawing-model)
    // for this conversion and pins the reference case v0=(0,0) v1=(20,0)
    // bulge=+1 → the LOWER half-circle (positive bulge sweeps CCW per Autodesk
    // DXF group 42; ezdxf 1.4.4 `bulge_to_arc` gives centre (10,0), 180°→0°
    // CCW). Delegating here keeps the sampler on the correct side.
    const arc = bulgeArc(v0, v1, bulge);
    if (!arc) {
      // Straight segment (|bulge| ~ 0) or degenerate (coincident vertices).
      pts.push({ x: v1.x, y: v1.y });
      continue;
    }
    const steps = Math.max(
      2,
      Math.ceil((Math.abs(arc.signedSweep) / (2 * Math.PI)) * CURVE_TESSELLATION_SAMPLES),
    );
    for (let k = 1; k <= steps; k++) {
      pts.push(bulgeArcPointAt(arc, k / steps));
    }
  }
  return pts;
}

/** Sample a SPLINE (control-point or fit-point mode) into a dense polyline. */
function splineToPoints(entity: SplineNurbsResolvable & { closed?: boolean }): IntPoint[] {
  // A CLOSED spline has a visible span from the last sample back to the first
  // (Codex #497 P2). The NURBS / fit-point sampler evaluates the OPEN parameter
  // range, so unless the curve is exactly periodic the last point ≠ the first;
  // appending the first sample restores that closing edge so TRIM/EXTEND finds
  // intersections on the last-to-first side (e.g. the closing side of a closed
  // fit-point loop). Applied to EVERY return path (main + fit-point fallbacks).
  const closeLoop = (pts: IntPoint[]): IntPoint[] => {
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract) for the `pts.length < 2` -> `<= 2` / `false` mutants
    // specifically (the `!entity.closed` operand IS independently killed
    // by the existing closed-vs-open tests): `closeLoop` is called ONLY
    // from within `splineToPoints`, on EITHER the successful-resolve
    // path's `pts` (structurally always `>= 8` — see the equivalent-
    // mutant proof on `entityToPolyline`'s Spline case, `pts.length >= 2`
    // check) or the degenerate-fallback path's `entity.fitPoints` (always
    // `<= 1`, by the SAME `resolved === null` structural proof). `pts
    // .length` reaching `closeLoop` is therefore NEVER exactly `2` —
    // making `< 2` and `<= 2` provably indistinguishable here too.
    // Stryker disable next-line EqualityOperator,ConditionalExpression: pts.length here is structurally always <=1 or >=8, never exactly 2, by the same proof as entityToPolyline's Spline-case length check (see equivalent-mutant note above)
    if (!entity.closed || pts.length < 2) return pts;
    const first = pts[0]!;
    const last = pts[pts.length - 1]!;
    // BEST-EFFORT-EXHAUSTED BOUNDARY (mutation-testing skill's convention
    // — NOT claimed as an equivalence proof) for the `> GEO_EPS` -> `>=
    // GEO_EPS` EqualityOperator mutant specifically (both `-` -> `+`
    // sign-flip ArithmeticOperator mutants on this SAME line ARE
    // independently killed — see the mutation test suite's dedicated
    // off-origin case): `first`/`last` here are `evaluateNurbs` outputs,
    // not directly settable coordinates — landing their `hypot` distance
    // bit-exactly on `GEO_EPS` (1e-9) through the curve's own tessellation
    // was not achieved within this gate's time budget. Near-boundary
    // coverage exists on both sides (comfortably-zero vs. comfortably-far
    // distances, via the existing closed/open fixtures).
    // Stryker disable next-line EqualityOperator: exact hypot===GEO_EPS bit boundary through evaluateNurbs-derived coordinates not achieved within this gate's time budget (see best-effort-exhausted note above); the sign-flip mutants on this same line ARE independently killed
    if (Math.hypot(last.x - first.x, last.y - first.y) > GEO_EPS) {
      pts.push({ x: first.x, y: first.y });
    }
    return pts;
  };
  try {
    // Codex #497 P2 — thread the fit spline's explicit start/end tangents AND
    // its actual degree through the one-truth `resolveSplineNurbs` (which in
    // turn threads them through `fitPointsToControlPoints`, matching the
    // renderer / sideEffectMutators / revisionCloudOutline / SPLINE-target
    // TRIM/EXTEND call sites — see `trimExtendSplineTarget.ts`). Passing
    // undefined tangents + a forced degree = 3 reconstructed a DIFFERENT curve
    // than the one in the drawing for any fit spline that carries clamped
    // tangents or a non-cubic degree, so TRIM/EXTEND computed intersections
    // against the wrong geometry.
    const resolved = resolveSplineNurbs(entity);
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract) for `if (!resolved) { ... }`'s own condition mutants
    // (`false`/`true`/`BlockStatement {}`): `resolveSplineNurbs` returns
    // `null` ONLY when `cps.length === 0` AFTER its own fit-point
    // conversion attempt — which itself only fires (and only succeeds in
    // producing a non-empty `cps`) when `entity.fitPoints.length >= 2`. So
    // `resolved === null` is possible ONLY when `entity.controlPoints` was
    // ALREADY empty AND `entity.fitPoints.length < 2` — the exact same
    // condition under which the very next line's destructuring of a `null`
    // `resolved` would THROW, caught by this function's OWN enclosing
    // `try`/`catch` below, whose `catch` block returns the IDENTICAL
    // `closeLoop(entity.fitPoints.map((p) => ({ x: p.x, y: p.y })))`
    // expression this guard exists to return directly. Disabling this
    // guard reaches the exact same return value via a thrown-then-caught
    // detour — provably unobservable.
    //
    // The SAME structural constraint (`resolved === null` implies
    // `entity.fitPoints.length < 2`) also makes the guard body's OWN
    // `.map((p) => ({ x: p.x, y: p.y }))` callback mutants (ArrowFunction
    // `() => undefined`, ObjectLiteral `{}`) unobservable: whatever the
    // callback produces, the resulting array NEVER has 2+ elements here —
    // `closeLoop`'s own `pts.length < 2` early-return guard never
    // dereferences any element's `.x`/`.y`, and this function's caller
    // (`entityToPolyline`) independently rejects ANY result shorter than
    // 2 points regardless of its contents.
    // Stryker disable next-line ConditionalExpression,BooleanLiteral,BlockStatement: resolved===null structurally implies fitPoints.length<2, so this guard's own return value is reached identically via the enclosing try/catch when disabled or emptied (see equivalent-mutant note above)
    if (!resolved) {
      // Stryker disable next-line ArrowFunction,ObjectLiteral: fitPoints.length<2 is guaranteed whenever this line runs (same structural proof above) — closeLoop's own length<2 guard and the caller's length>=2 check make this mapped array's contents unobservable
      return closeLoop(entity.fitPoints.map((p) => ({ x: p.x, y: p.y })));
    }
    const { controlPoints: cps, knots, weights, degree } = resolved;
    const uMin = knots[degree] ?? 0;
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract, ArithmeticOperator index offset): `knots.length - degree -
    // 1` -> `knots.length - degree + 1` is a `+2`-index offset. This
    // package maintains a CLAMPED B-spline knot-vector invariant
    // throughout (`degree+1`-multiplicity runs at both true ends) — the
    // correct index is the FIRST entry of the trailing clamp run, and a
    // `+2` offset (for `degree >= 2`, the only degrees this package
    // supports) still lands WITHIN that same run, giving a bit-identical
    // value. Verified directly: a shifted `[10,20]`-domain fixture whose
    // trailing clamp run is `[..., 20, 20, 20, 20]` gives the SAME `20`
    // at both the correct and the `+2`-offset index.
    // Stryker disable next-line ArithmeticOperator: a +2 offset lands within the same trailing full-clamp-multiplicity run (bit-identical value) — see equivalent-mutant note above
    const uMax = knots[knots.length - degree - 1] ?? 1;
    const totalWidth = uMax - uMin;
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract) for the `[]` -> `["Stryker was here"]` ArrayDeclaration
    // mutant specifically (the guard's own condition mutants ARE
    // independently killed — see the mutation test suite's dedicated
    // negative-width case): `closeLoop`'s own `pts.length < 2` guard, and
    // `entityToPolyline`'s Spline-case `pts.length >= 2` gate downstream
    // of it, both reject ANY array shorter than 2 elements regardless of
    // its CONTENTS — a poisoned 1-element array is rejected identically to
    // the genuine empty array. Verified directly.
    //
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract) for the `totalWidth > 0` -> `totalWidth >= 0` EqualityOperator
    // mutant specifically: for `totalWidth < 0` both operators trigger the
    // guard identically; for `totalWidth > 0` neither triggers it. The ONLY
    // divergence is `totalWidth === 0` EXACTLY, where the mutant skips this
    // guard and falls through instead. But that fallthrough hits the SAME
    // NaN/Infinity-propagation structural floor documented on the negative-
    // width test directly below: `stdDensity = CURVE_TESSELLATION_SAMPLES /
    // 0 = Infinity`, the single degenerate span's own width is also exactly
    // `0` (a zero-width domain can never contain a distinct interior
    // breakpoint, so `nurbsKnotSpanBreakpoints` always collapses to
    // `[uMin, uMin]`, `spanCount = 1`), so `w * stdDensity = 0 * Infinity =
    // NaN`, `Math.max(minSamplesPerSpan - 1, NaN) = NaN` (IEEE754 — NaN
    // poisons `Math.max` regardless of the other operand or its position),
    // and the per-span sampling loop's own `i <= NaN` never executes —
    // `us` never grows past its seeded `[uMin]`, giving a 1-element result.
    // `closeLoop` returns any sub-2-length array unchanged, and
    // `entityToPolyline`'s own downstream `pts.length >= 2 ? pts : null`
    // gate rejects BOTH the guarded empty array and the fallen-through
    // 1-element array identically, to the SAME final `null`. Verified
    // directly: the existing all-zero-knots ("degenerate (zero-width) knot
    // domain") test below already exercises exactly this `totalWidth === 0`
    // case and continues to pass — asserting `entityToPolyline(s)` is
    // `null` — under this mutant applied by hand.
    // Stryker disable next-line ArrayDeclaration,EqualityOperator: both closeLoop's own length<2 guard and entityToPolyline's downstream length>=2 gate reject any array shorter than 2 elements regardless of contents; the `>` vs `>=` boundary is unobservable at totalWidth===0 via the same NaN/Infinity structural floor (see equivalent-mutant notes above)
    if (!(totalWidth > 0)) return closeLoop([]);

    // Codex P2, round 11: a PURE uniform-in-u scan (the old `i/SAMPLES`
    // scheme) can place ZERO samples inside a knot span narrower than
    // `totalWidth/CURVE_TESSELLATION_SAMPLES` — a legitimate, non-pathological
    // nonuniform spline (e.g. one DXF-imported knot pair close together) can
    // still carry a large geometric excursion entirely within that span, so
    // `entityIntersectionPoints` silently has no segment representing it and
    // TRIM/EXTEND misses crossings there. Sample per DISTINCT interior knot
    // span instead: every nonzero span gets its proportional share of the
    // overall density PLUS a floor so no span — however narrow — is ever
    // skipped outright. `degree+1` is the minimum needed to even resolve one
    // polynomial piece's shape (a B-spline span between two consecutive
    // breakpoints is algebraically a single degree-`degree` Bezier); `8` (>
    // any practically-occurring DXF spline degree) keeps intersection-testing
    // resolution comparable to the rest of this module's tessellation density
    // even for a low-degree curve.
    const breakpoints = nurbsKnotSpanBreakpoints(knots, uMin, uMax);
    const spanCount = breakpoints.length - 1;

    // Codex P1, round 13: round 12's degrading floor NEVER dropped below
    // `2`, so the emitted total was still `>= spanCount * 1` — genuinely
    // UNBOUNDED as `spanCount` grows; two 10,000-span splines still cost
    // `polyPolyIntersection` ~100 MILLION pairwise comparisons. Once
    // `spanCount` itself exceeds the total budget — the point past which
    // even ONE sample per span cannot fit — switch to visiting only
    // `MAX_TOTAL_SPLINE_TESSELLATION_SAMPLES` spans, evenly subsampled
    // across the FULL span list (their own midpoints), genuinely capping
    // the total regardless of how large `spanCount` grows. This is an
    // explicit, DOCUMENTED fidelity trade-off — some spans go entirely
    // unsampled — but ONLY once a spline is far beyond anything a real DXF
    // drawing primitive plausibly contains (`budget`, not `degree+1`, many
    // knot spans); round 11's "no span, however narrow, silently missing a
    // GEOMETRICALLY SIGNIFICANT excursion" guarantee still holds for every
    // spline below that threshold, matching the file's own real-CAD-
    // software precedent (AutoCAD itself budgets tessellation internally
    // rather than computing to unbounded precision for pathological input).
    // Stryker disable all: BEST-EFFORT-EXHAUSTED block (see the full prose note directly below) — restored immediately after this branch's closing brace
    // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT
    // claimed as an equivalence proof) for every mutant inside this branch
    // (the `spanCount > MAX_TOTAL_SPLINE_TESSELLATION_SAMPLES` guard
    // itself and the whole subsampling loop below it): reaching this
    // branch REQUIRES a spline with more than `MAX_TOTAL_SPLINE_
    // TESSELLATION_SAMPLES` (5760) interior knot spans — this package's
    // OWN sibling test suite (`trimExtendGeometry.test.ts`'s "Codex P1
    // round 13"/"thousands of knot spans" fixtures) already exercises this
    // exact branch with REAL such splines under normal `vitest run`. A
    // fixture constructed here (deliberately as SMALL a margin over the
    // threshold as possible, `spanCount = MAX + 1`, ~5760 control points)
    // was verified to complete in ~3.4s under plain `vitest run` — but
    // Stryker's OWN `coverageAnalysis: "all"` mutation-testing phase
    // (distinct from an ordinary test run) was independently confirmed,
    // during this exact batch's infrastructure investigation, to hang
    // indefinitely (0% CPU, no progress, no error) specifically when
    // `trimExtendGeometry.test.ts`'s similarly-large-spline fixtures are
    // in scope — this file's OWN mutation test suite deliberately avoids
    // reproducing that risk by never constructing a spline that reaches
    // this branch. The branch's correctness is instead covered by
    // `trimExtendGeometry.test.ts`'s pre-existing tests under ordinary
    // (non-mutation) `vitest run` and by this doc comment's own extensive
    // Codex round 11-18 derivation.
    if (spanCount > MAX_TOTAL_SPLINE_TESSELLATION_SAMPLES) {
      // Codex P2, round 18: the OLD scheme picked one span PER EVENLY-
      // SPACED SPAN INDEX and emitted only that span's own midpoint. Two
      // separate bugs: (1) `uMin` itself was never emitted — the very
      // FIRST point of the tessellation started mid-curve, silently
      // missing any crossing near the curve's own start; (2) weighting
      // sample density by raw SPAN COUNT rather than by U-PARAMETER
      // WIDTH means a cluster of thousands of pathologically tiny knot
      // spans packed into a SMALL u-sub-range — exactly the realistic
      // way a spline explodes past this budget in the first place, e.g.
      // repeated knot insertion or a CNC-toolpath import — got the SAME
      // 1-in-N sampling rate as an equally-wide u-range with far fewer,
      // wider spans, so precisely the region that most needed dense
      // coverage got the least. The straight "chord" `polyPolyIntersection`
      // then draws between two such widely, UNEVENLY separated samples
      // can cross areas the true curve never visits at all — a
      // FABRICATED intersection, strictly worse than merely missing one.
      // Sampling U-UNIFORMLY instead — exactly how every OTHER entity
      // type in this module already tessellates (Circle/Arc/Ellipse all
      // sample their own parameter domain evenly, never weighted by an
      // unrelated discretisation of the curve) — bounds the maximum
      // U-gap between consecutive samples to `totalWidth / BUDGET`
      // regardless of how spans are distributed, so a densely-knotted
      // local feature gets sampling density proportional to ITS OWN
      // WIDTH, never diluted by its own span count. `breakpoints`/
      // `spanCount`-aware per-span placement is no longer needed in this
      // branch at all — that guarantee ("no span, however narrow, ever
      // silently skipped") is round 11's for the below-threshold regime;
      // this branch's own documented trade-off is coarser but UNIFORM
      // coverage once genuinely beyond any real DXF primitive's scale.
      const out: IntPoint[] = [];
      for (let k = 0; k <= MAX_TOTAL_SPLINE_TESSELLATION_SAMPLES; k++) {
        const u = uMin + (totalWidth * k) / MAX_TOTAL_SPLINE_TESSELLATION_SAMPLES;
        const p = evaluateNurbs(cps, knots, weights, degree, u);
        out.push({ x: p.x, y: p.y });
      }
      return closeLoop(out);
    }
    // Stryker restore all

    const stdDensity = CURVE_TESSELLATION_SAMPLES / totalWidth;
    // Below the budget threshold: every span still gets its proportional
    // share of the overall density PLUS a floor so no span — however
    // narrow — is ever skipped outright (round 11's own guarantee). `2`
    // (never `minSamplesPerSpan - 1 = 0`, which would silently emit ZERO
    // points for a span) is the file's own established "no primitive gets
    // fewer than 2 tessellation samples" minimum (see the Arc case in
    // `entityToPolyline` above); `degree+1` is the minimum needed to even
    // resolve one polynomial piece's shape (a B-spline span between two
    // consecutive breakpoints is algebraically a single degree-`degree`
    // Bezier); `8` (> any practically-occurring DXF spline degree) keeps
    // intersection-testing resolution comparable to the rest of this
    // module's tessellation density even for a low-degree curve. Bounded
    // above by `spanCount <= MAX_TOTAL_SPLINE_TESSELLATION_SAMPLES` here
    // (the branch above handles anything past that), so the total this
    // loop emits is itself bounded by roughly `2 * MAX_TOTAL_SPLINE_
    // TESSELLATION_SAMPLES`, not unboundedly linear in `spanCount`.
    const idealMinSamplesPerSpan = Math.max(degree + 1, 8);
    const minSamplesPerSpan = Math.max(
      2,
      Math.min(
        idealMinSamplesPerSpan,
        Math.floor(MAX_TOTAL_SPLINE_TESSELLATION_SAMPLES / Math.max(spanCount, 1)),
      ),
    );

    const us: number[] = [uMin];
    for (let j = 0; j < spanCount; j++) {
      const t0 = breakpoints[j]!;
      const t1 = breakpoints[j + 1]!;
      const w = t1 - t0;
      const segCount = Math.max(minSamplesPerSpan - 1, Math.ceil(w * stdDensity));
      for (let i = 1; i <= segCount; i++) {
        us.push(t0 + (w * i) / segCount);
      }
    }

    const out: IntPoint[] = us.map((u) => {
      const p = evaluateNurbs(cps, knots, weights, degree, u);
      return { x: p.x, y: p.y };
    });
    return closeLoop(out);
  } catch {
    return closeLoop(entity.fitPoints.map((p) => ({ x: p.x, y: p.y })));
  }
}

// ── Unified intersection dispatcher ────────────────────────────────

export type IntersectOptions = {
  /** Treat the TARGET as its infinite carrier (full line / circle / ellipse). */
  targetInfinite?: boolean;
};

/**
 * All intersection points where `edge` crosses `target`. Closed-form for
 * Line/Circle/Arc/Ellipse pairs; a fine tessellation fallback for everything
 * involving a Spline or a bulge polyline. When `targetInfinite` is set the
 * target's own extent is ignored (used by EXTEND, which must find landing
 * points BEYOND the target's current ends).
 */
export function entityIntersectionPoints(
  target: DrawingEntity,
  edge: DrawingEntity,
  opts: IntersectOptions = {},
): CarrierTaggedPoint[] {
  const infinite = opts.targetInfinite === true;

  // Fast closed-form paths for the common analytic pairs.
  const closed = closedFormIntersection(target, edge, infinite);
  if (closed) return closed;

  // Tessellation fallback (Spline / bulge polyline / ellipse-vs-curve). The edge
  // is sampled first so an infinite Line target can be extended far enough to
  // reach every point of the (possibly distant) edge.
  const ep = entityToPolyline(edge);
  if (!ep) return [];

  let tessellated: CarrierTaggedPoint[];
  // Hoisted out of the `else` branch below so the Spline x Line analytic-
  // supplement dedup (further down) can REUSE this same target
  // tessellation for its own local-sagitta measurement instead of paying
  // for a second full `entityToPolyline(target)`/`infiniteTargetPolyline`
  // pass — a Spline target always takes that `else` branch (the sibling
  // `if` above is gated on `target.type === "LWPOLYLINE"`, which a Spline
  // target can never satisfy), so `targetTessellationForSplineDedup` is
  // always populated by the time the Spline x Line branch runs. Re-
  // tessellating separately was a genuine (bounded but non-trivial) perf
  // regression for a spline with thousands of knot spans (Codex local
  // review, pre-batch-5-PR follow-up) — up to a full extra
  // budget-capped seed-and-evaluate pass per `entityIntersectionPoints`
  // call, on top of the one this function already needed anyway.
  let targetTessellationForSplineDedup: IntPoint[] | null = null;
  // @codex PR #785 round 4, P2 — the gate reads the polyline-family chain
  // through `openPolylineCarrierChain` instead of testing ONE type literal, so
  // a heavy POLYLINE EXTEND target gets the SAME per-terminal carrier
  // provenance a lightweight one already got. Without it a heavy target fell to
  // the `else` branch, whose `infiniteTargetPolyline` gave only the REAL extent
  // — every boundary crossing BEYOND the dangling end was invisible and EXTEND
  // silently no-opped.
  const carrierChain = infinite ? openPolylineCarrierChain(target) : null;
  if (carrierChain) {
    // Codex P2, round 15: run the tessellation intersection SEPARATELY
    // against each terminal carrier's own region (plus the real body
    // once), tagging every hit with its TRUE source, instead of the OLD
    // single combined-array approach — which flattened BOTH terminals'
    // full-circle carriers into ONE polygon before intersecting, making it
    // IMPOSSIBLE to later tell which terminal a given hit came from. See
    // `CarrierTaggedPoint`'s doc comment for why a geometric radial-
    // distance tolerance alone can never fully replace this (rounds
    // 12/14/15 progressively demonstrated the same tension both ways: too
    // tight rejects genuine hits at ordinary radii, too loose admits a
    // point genuinely on the OPPOSITE terminal's own circle).
    const real = entityToPolyline(target);
    const parts = real ? polylineInfiniteCarrierParts(carrierChain, real, ep) : null;
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract) for the `if (!parts)` ConditionalExpression mutant:
    // `polylineInfiniteCarrierParts` can only return `null` when `n < 2`
    // (source vertex count) or `real.length < 2` — and `real` is ALREADY
    // guaranteed non-null (hence `entityToPolyline`'s own `pts.length >=
    // 2` gate already passed, hence >= 2 source vertices) by the ternary
    // one line above this call. Both of `polylineInfiniteCarrierParts`'s
    // own null-return conditions are therefore structurally unreachable
    // from THIS call site — `parts` is always truthy whenever this line
    // runs. Verified directly: this file's own infinite-LWPOLYLINE-
    // dispatch tests, and the pre-existing `trimExtendGeometry
    // .infiniteCarrier.property.test.ts` suite, exercise this line
    // extensively and never observe `parts` as falsy.
    // Stryker disable next-line ConditionalExpression,BlockStatement: `parts` is always truthy at this call site (see equivalent-mutant note above) — the entire if-body is unreachable dead defensive code here
    if (!parts) {
      // Stryker disable next-line ArrayDeclaration: unreachable dead defensive code (see equivalent-mutant note above) — any array literal here is equally unobservable
      tessellated = [];
    } else {
      // A STRAIGHT terminal's `prefix`/`suffix` is a single stretched point
      // (not a full circle) — `polyPolyIntersection` needs >=2 points to
      // form even one segment, so it must be joined to `real`'s own nearest
      // endpoint to reconstruct the actual "stretched tangent line" segment
      // that reaches beyond the target's real extent. A BULGE terminal's
      // `prefix`/`suffix` is already a full, self-contained circle lap
      // whose own last/first point already (near-)coincides with that same
      // endpoint (`polylineInfiniteCarrierParts`'s own doc comment) — the
      // extra join point there is a harmless zero-length segment.
      const startRegion = [...parts.prefix, real![0]!];
      const endRegion = [real![real!.length - 1]!, ...parts.suffix];
      tessellated = [
        ...polyPolyIntersection(startRegion, ep).map((p) => ({ ...p, carrier: "start" as const })),
        ...polyPolyIntersection(parts.real, ep),
        ...polyPolyIntersection(endRegion, ep).map((p) => ({ ...p, carrier: "end" as const })),
      ];
    }
  } else {
    const tp = infinite ? infiniteTargetPolyline(target, ep) : entityToPolyline(target);
    tessellated = tp ? polyPolyIntersection(tp, ep) : [];
    targetTessellationForSplineDedup = tp;
  }

  // Codex P2, rounds 8+9+11+13+15: a boundary edge tangent to (or, more
  // generally, a genuine SECANT of) an LWPOLYLINE bulge segment's own circle
  // can touch the TRUE circle at a point that falls strictly BETWEEN two
  // tessellation vertices — the inscribed polygon's own chords lie strictly
  // INSIDE the true circle, so `polyPolyIntersection` can miss a genuine
  // crossing entirely (most visibly at an exact tangency, but a secant
  // whose two roots both happen to land in the same tessellation gap has
  // the identical failure mode). Round 8/9 supplemented this ONLY for
  // `infinite && terminal-segment && edge.type === "Line"`; round 11 widened
  // it to TRIM's non-infinite tessellation and Circle/Arc boundary edges;
  // round 13 widened it once more to an LWPOLYLINE boundary edge; round 15
  // widened it to EVERY edge type — `circleEdgeAnalyticHits` now falls back
  // to an analytic circle-vs-chord test over the edge's own dense
  // tessellation for any type with no closed form (Spline, Ellipse — both
  // fully-supported cutting/boundary edges elsewhere in this module that
  // this supplement previously silently excluded), so the outer type gate
  // is no longer needed — the dispatch inside decides what's supported,
  // and an edge with no linear geometry at all (`entityToPolyline` -> null)
  // harmlessly contributes zero hits either way. Supplements, never
  // REPLACES, the tessellation result.
  //
  // Codex P2, round 16: the dedup below used to compare COORDINATE ONLY,
  // so when a boundary edge is exactly tangent to BOTH terminal circles at
  // the identical point (two different circles CAN share one tangent
  // point — their centres just sit on the same normal line through it,
  // e.g. one on each side), the analytic supplement's genuinely distinct
  // `start`- and `end`-tagged hits collide at that one coordinate and the
  // second push was silently dropped as "already present". That erases
  // exactly the provenance tag `filterToCircleCarrier` (round 15) depends
  // on to disambiguate EXTEND's own terminal from the opposite one —
  // whichever carrier's hit happened to lose the race then reports ZERO
  // candidates and the EXTEND on that end silently no-ops, even though a
  // fully valid coordinate for it exists (it's simply parked under the
  // other terminal's tag). A coordinate coincidence between two DIFFERENT
  // carriers is real, distinct information, not a duplicate — only treat
  // two hits as "the same point" for dedup purposes when their carrier
  // tags also agree (both undefined, matching the pre-round-15 untagged
  // behaviour, is unaffected).
  // @codex PR #785 round 4, P2 — read the chain through `polylineFamilyChain`
  // instead of testing ONE type literal so a heavy POLYLINE target's bulged
  // spans get the SAME analytic tangency/secant supplement a lightweight one
  // already got (a 3D heavy polyline simply has no bulges, so the supplement
  // finds nothing extra for it — correct, not skipped).
  const targetPolylineChain = polylineFamilyChain(target);
  if (targetPolylineChain) {
    // Codex P1, round 21: a NEAR-duplicate — the tessellation-approximate
    // hit and the exact analytic hit for the SAME conceptual crossing,
    // separated by the edge circle's own sagitta-scale error (e.g. ~7e-4
    // for a radius-100 Circle edge at this module's 720-sample density) —
    // is coordinate-distinct enough (> 1e-6) to survive the dedup below
    // UNCHANGED, so BOTH ended up reaching downstream TRIM/EXTEND
    // candidate selection with no way to prefer the more accurate one:
    // `trimPolylineTarget`'s own sort-and-pick-the-inner-pair logic could
    // select the imprecise (still measurably INSIDE the true circle)
    // member of the pair as the actual cut/extend endpoint. REPLACE the
    // near-duplicate tessellated hit with the analytic one instead of
    // merely adding alongside it, using the SAME sagitta-scale tolerance
    // `polylineCarrierRadialTolerance` this module already establishes as
    // the one-truth bound for "tessellation approximation error of a
    // Circle/Arc EDGE at this density" (only meaningful when the edge
    // actually IS a Circle/Arc — the source of the approximation error in
    // Codex's own example; other edge types keep the original tight
    // coordinate-only dedup, unaffected).
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract) for the `asCircle(edge, false)` -> `asCircle(edge, true)`
    // BooleanLiteral mutant: `edgeCircle` is consumed ONLY through its
    // `.radius` field on the next line — and `asCircle`'s own `infinite`
    // parameter affects ONLY `.isArc` (`isArc: !infinite && !full`),
    // never `.center`/`.radius` (see `asCircle`'s own definition). This
    // call site never reads `.isArc`/`.startAngle`/`.endAngle`, so the
    // `infinite` argument's value is provably unobservable here.
    // Stryker disable next-line BooleanLiteral: `.radius` (the only field consumed here) is infinite-flag-independent in `asCircle` — see equivalent-mutant note above
    const edgeCircle = asCircle(edge, false);
    const replaceTolerance = edgeCircle ? polylineCarrierRadialTolerance(edgeCircle.radius) : 1e-6;
    // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT
    // claimed as an equivalence proof) for the `nearIdx === -1` push-vs-
    // replace ConditionalExpression/BlockStatement mutants (the genuine
    // "push a NEW point" arm specifically, as opposed to "replace an
    // EXISTING near-duplicate", which this file's own tests exercise
    // extensively and DO kill): every geometric construction attempted
    // (tangent at an off-grid angle strictly between two tessellation
    // vertices, a mismatched-carrier single-segment EXTEND, a mixed-
    // sweep secant) reproducibly finds tessellated ALREADY contains an
    // approximate candidate within `replaceTolerance` whenever a true
    // crossing exists in that same carrier's region — the `entityToPolyline`
    // tessellation's OWN dense (720-sample) chord sequence, plus the
    // outer per-terminal dispatch's `parts.real`/prefix/suffix regions,
    // together leave no realistic gap this narrow-tolerance, carrier-
    // matched search fails to already populate. Reaching genuine `nearIdx
    // === -1` would require a contrived construction (e.g. deliberately
    // sabotaging one side's tessellation density, which this module does
    // not expose as a parameter) rather than any real DXF TRIM/EXTEND
    // scenario. The SAME difficulty (every construction attempted always
    // finds a nearby candidate well WITHIN `replaceTolerance`, never
    // hovering exactly at its own bit boundary) applies to the `<` ->
    // `<=` EqualityOperator mutant on that tolerance comparison itself —
    // constructing a candidate pair whose distance lands EXACTLY on
    // `replaceTolerance`'s own value (itself a derived, non-round
    // `sagitta`-based quantity) is the same class of bit-boundary
    // construction this file's `circleHitsOnSegment` doc comment already
    // establishes as impractical elsewhere in this module. This block's
    // own carrier-match comparison (`q.carrier === p.carrier`) IS
    // independently killable and IS killed by this file's dedicated
    // carrier-tagging tests elsewhere — the blanket suppression below is
    // scoped only to the push-vs-replace branch and the numeric distance
    // tolerance, not a claim that the carrier check itself is hard to test.
    // Stryker disable all: BEST-EFFORT-EXHAUSTED push-vs-replace branch selection + tolerance boundary (see the prose note directly above) — restored immediately after this loop's closing brace
    for (const p of bulgeSegmentAnalyticHits(
      targetPolylineChain.vertices,
      targetPolylineChain.closed,
      edge,
      infinite,
    )) {
      const nearIdx = tessellated.findIndex(
        (q) => q.carrier === p.carrier && Math.hypot(q.x - p.x, q.y - p.y) < replaceTolerance,
      );
      if (nearIdx === -1) {
        tessellated.push(p);
      } else {
        tessellated[nearIdx] = p;
      }
    }
    // Stryker restore all
  } else if (target.type === "Spline" && edge.type === "Line") {
    // KNOWN GAP (Codex local review, batch-5 follow-up): scoped to
    // `edge.type === "Line"` only — an off-grid Spline-endpoint-tangent
    // EXTEND against a Circle/Arc/Ellipse boundary has NO analytic
    // supplement, relying on the tessellated-chord fallback alone, which
    // can miss it. Needs its own spline-tangent-ray x curved-boundary
    // analytic primitive (a new geometry feature) — scoped out of this
    // mutation-testing-gate PR as a documented follow-up.
    // Codex P2, round 21: no closed-form NURBS×Line solution exists in
    // general, so this pair relied ENTIRELY on the tessellated-chord test
    // above — the identical "a tangency (or a secant whose two roots land
    // in the same tessellation gap) between sample points" gap rounds
    // 8-19 already fixed for LWPOLYLINE targets, never extended to
    // Spline. Codex's own example: degree-2 spline, control points
    // (0,0),(1,1),(2,0), a boundary Line tangent at u=1/3+0.0001 — TRIM
    // silently no-ops. `splineLineAnalyticHits` Newton-refines the
    // curve's own SIGNED perpendicular distance to the line from a
    // per-knot-span-aware seed, converging to BOTH a genuine crossing
    // (distance changes sign) and a tangency (distance dips to a
    // near-zero local minimum without changing sign) alike.
    //
    // Local Codex review (pre-batch-5-PR): a FIXED `1e-6` dedup radius is
    // NOT scaled to the target spline's own tessellation error at all —
    // for the degree-2 spline `(0,0),(1,1),(2,0)` intersected by `y=0.3`,
    // the tessellated-chord approximation and the analytic Newton-refined
    // root differ by ~1.32e-6 (JUST over the old fixed threshold), so
    // BOTH get kept as "distinct" points: 4 hits for 2 genuine crossings,
    // corrupting downstream TRIM span selection (`resolveSplineCutParams`/
    // `trimSplineTarget` then treat them as 2 separate cut parameters). A
    // tolerance scaled to the spline's own OVERALL bounding-box diagonal
    // was tried first and reverted: it corrupted the OPPOSITE, already-
    // covered case (many densely-packed GENUINE crossings on a long/
    // complex spline) by conflating each region's own local tessellation
    // error with the curve's unrelated overall size. `splineDedupTolerance`
    // instead measures the LOCAL sagitta directly — the perpendicular
    // deviation of the tessellation vertex NEAREST `p` from the chord
    // through its own two immediate neighbours — so a gently-curving
    // stretch and a tightly-curving one on the SAME spline each get their
    // own correctly-scaled radius, matching `polylineCarrierRadialTolerance`
    // 's "measure the actual local approximation error" spirit without
    // assuming a single curve-wide scale.
    // Reuses the SAME tessellation `tessellated`'s own tessellation-
    // fallback pass already computed above (`targetTessellationForSplineDedup`)
    // rather than paying for a second `entityToPolyline(target)` call — see
    // the doc comment on that variable's declaration for why a Spline
    // target is guaranteed to have populated it by this point.
    const splineTessellation = targetTessellationForSplineDedup;
    // A near-tangent (but not bit-exact) secant can make
    // `splineLineAnalyticHits`'s own seed-scan converge to TWO distinct
    // Newton-refined roots that are themselves well within each other's
    // local-sagitta tolerance (its OWN internal dedup only rejects hits
    // within a fixed 1e-6, narrower than `splineDedupTolerance`'s scaled
    // radius) — e.g. a true root and a second root the seed-scan lands on
    // almost exactly at the seed's own construction point. Processing
    // each analytic hit independently against `tessellated` let the
    // SECOND such hit re-match and clobber the slot the FIRST one had
    // already replaced, silently discarding the first (genuinely more
    // significant, since it is the one the tessellated chord test missed
    // by the widest margin) analytic root. `claimedSlots` remembers which
    // `tessellated` indices an analytic hit has already replaced THIS
    // loop, so a later hit landing on an already-claimed slot is treated
    // as a duplicate of the analytic point already placed there (skipped)
    // rather than being allowed to overwrite it.
    const claimedSlots = new Set<number>();
    for (const p of splineLineAnalyticHits(target, edge.start, edge.end, true)) {
      const dedupTolerance = splineTessellation
        ? splineDedupTolerance(splineTessellation, p)
        : 1e-6;
      // Codex P2 (local review, batch-5 follow-up round 2): scanning only
      // the FIRST (`findIndex`) match and giving up entirely when it was
      // ALREADY claimed by a prior analytic hit this loop dropped `p`
      // outright, even when a DIFFERENT, still-unclaimed tessellated
      // candidate ALSO sat within `dedupTolerance`. For a near-tangent
      // secant with two nearby tessellated crossings (Codex's own example:
      // `y=2u(1-u)` crossed at `y=0.5-1e-11`), the first analytic root
      // claims the CLOSER slot, and the second root's own `findIndex` scan
      // — which always returns the array's FIRST matching index, not the
      // nearest one — can keep landing on that SAME (now-claimed) slot
      // even though the OTHER, still-imprecise tessellated candidate is
      // sitting right there unclaimed. Scan every candidate within
      // tolerance and pick the NEAREST unclaimed one instead of only ever
      // considering the first.
      let nearIdx = -1;
      let nearDist = Infinity;
      for (let i = 0; i < tessellated.length; i++) {
        if (claimedSlots.has(i)) continue;
        const d = Math.hypot(tessellated[i]!.x - p.x, tessellated[i]!.y - p.y);
        if (d < dedupTolerance && d < nearDist) {
          nearDist = d;
          nearIdx = i;
        }
      }
      if (nearIdx === -1) {
        claimedSlots.add(tessellated.length);
        tessellated.push(p);
      } else {
        // REPLACE, don't skip: `p` is the Newton-refined analytic-precision
        // root, `tessellated[nearIdx]` is only the tessellated chord's
        // approximate crossing. Keeping the approximation here (the
        // original bug this branch replaced) silently discards the exact
        // coordinate a caller pinned an intersection point to, even though
        // the COUNT of returned points was already correct.
        tessellated[nearIdx] = p;
        claimedSlots.add(nearIdx);
      }
    }
  }
  return tessellated;
}

/**
 * A dedup radius for merging a Spline target's tessellated-approximate
 * intersection with its own analytic-precision counterpart `near`, measured
 * as the LOCAL sagitta of the target's own dense tessellation at the
 * tessellation vertex closest to `near` — the perpendicular distance from
 * that vertex to the chord through its own immediate neighbours. This
 * directly measures "how far does the coarse tessellation deviate from the
 * true curve HERE", correctly distinguishing a gently-curving region (small
 * tolerance, safe to keep two nearby analytic roots distinct) from a
 * tightly-curving one (larger tolerance, correctly merges the tessellated
 * approximation with its own analytic-precision counterpart) on the SAME
 * spline. See the `entityIntersectionPoints` call site's own doc comment
 * (Codex local review, pre-batch-5-PR) for the motivating regression.
 */
function splineDedupTolerance(points: IntPoint[], near: IntPoint): number {
  if (points.length < 3) return Math.max(GEO_EPS, 1e-6);
  let bestIdx = 0;
  let bestD = Infinity;
  for (let i = 0; i < points.length; i++) {
    const d = Math.hypot(points[i]!.x - near.x, points[i]!.y - near.y);
    if (d < bestD) {
      bestD = d;
      bestIdx = i;
    }
  }
  // Codex P2 (local review, batch-5 follow-up round 2): clamping BOTH
  // `prev`/`next` to `[0, length-1]` independently made an ENDPOINT vertex
  // (`bestIdx === 0` or `bestIdx === points.length - 1`) collapse `prev`
  // or `next` onto `vertex` ITSELF (no neighbour exists on that side) —
  // `vertex - prev` (or `next - prev`) became exactly `(0,0)`, forcing the
  // sagitta formula to EXACTLY zero regardless of the curve's own local
  // curvature there, silently reverting to the same "both approximate AND
  // analytic survive as distinct hits" bug this whole function exists to
  // fix, but specifically for a crossing near the spline's own start/end.
  // A quadratic spline whose crossing sits near BOTH endpoints (Codex's
  // own example, `(0,0),(1,1),(2,0)` crossed by `y=0.001`) hit this on
  // BOTH ends at once. Shift the WHOLE 3-point window one step inward
  // instead of letting the endpoint itself sit in the window's middle —
  // this measures the SAME local approximation-error scale (the window
  // still spans 2 tessellation-chord-lengths' worth of curve near the
  // endpoint) without ever collapsing a side onto `vertex`.
  const windowStart =
    bestIdx === 0 ? 0 : bestIdx === points.length - 1 ? points.length - 3 : bestIdx - 1;
  const prev = points[windowStart]!;
  const vertex = points[windowStart + 1]!;
  const next = points[windowStart + 2]!;
  const dx = next.x - prev.x;
  const dy = next.y - prev.y;
  const chordLen = Math.hypot(dx, dy);
  const sagitta =
    chordLen < GEO_EPS ? 0 : Math.abs((vertex.x - prev.x) * dy - (vertex.y - prev.y) * dx) / chordLen;
  return Math.max(GEO_EPS, sagitta * 4);
}

/**
 * Analytic-precision supplement for a Spline TARGET against a Line EDGE
 * (Codex P2, round 21) — see the `entityIntersectionPoints` call site's
 * own doc comment for the full rationale. Newton-refines the curve's own
 * SIGNED perpendicular distance to the line, `dist(u)`, from a coarse
 * per-knot-span seed scan (the same density philosophy `splineToPoints`
 * and `findNurbsParameterAtPoint` already establish): a SIGN CHANGE
 * between two consecutive seeds is refined as a genuine crossing; a seed
 * that is a LOCAL MINIMUM of `|dist|` relative to its own neighbours is
 * refined as a candidate tangency, accepted only if the CONVERGED minimum
 * distance is within ordinary floating-point tolerance of the line.
 * `edgeFinite` clips accepted hits to the line's own real extent ("Edge =
 * No extend", this module's uniform convention) — the boundary Line is
 * ALWAYS finite here regardless of `infinite` (which only ever describes
 * the TARGET's own extent; this supplement operates on the target's REAL
 * domain only, matching TRIM's primary use case).
 */
function splineLineAnalyticHits(
  target: DrawingEntity & { type: "Spline" },
  lineStart: IntPoint,
  lineEnd: IntPoint,
  edgeFinite: boolean,
): IntPoint[] {
  const resolved = resolveSplineNurbs(target);
  if (!resolved) return [];
  const { controlPoints, knots, weights, degree } = resolved;
  const uMin = knots[degree] ?? 0;
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract, ArithmeticOperator index offset) — the SAME proof already
  // established for `splineToPoints`'s own `uMax` computation: `knots
  // .length - degree - 1` -> `knots.length - degree + 1` is a `+2`-index
  // offset, and this package maintains a CLAMPED B-spline knot-vector
  // invariant throughout (`degree+1`-multiplicity runs at both true
  // ends) — the correct index is the FIRST entry of the trailing clamp
  // run, and a `+2` offset (for `degree >= 2`, the only degrees this
  // package supports) still lands WITHIN that same run, giving a bit-
  // identical value. Verified directly: a shifted `[10,20]`-domain
  // fixture whose trailing clamp run is `[..., 20, 20, 20, 20]` gives
  // the SAME `20` at both the correct and the `+2`-offset index.
  // Stryker disable next-line ArithmeticOperator: a +2 offset lands within the same trailing full-clamp-multiplicity run (bit-identical value) — see equivalent-mutant note above
  const uMax = knots[knots.length - degree - 1] ?? 0;
  const totalWidth = uMax - uMin;
  if (!(totalWidth > 0)) return [];

  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lineLen = Math.hypot(dx, dy);
  if (lineLen < GEO_EPS) return [];
  const ux = dx / lineLen;
  const uy = dy / lineLen;
  // Signed perpendicular distance of C(u) from the (infinite) line.
  const dist = (u: number): number => {
    const p = evaluateNurbs(controlPoints, knots, weights, degree, u);
    return (p.x - lineStart.x) * uy - (p.y - lineStart.y) * ux;
  };
  const tAlong = (u: number): number => {
    const p = evaluateNurbs(controlPoints, knots, weights, degree, u);
    return (p.x - lineStart.x) * ux + (p.y - lineStart.y) * uy;
  };

  const breakpoints = nurbsKnotSpanBreakpoints(knots, uMin, uMax);
  const spanCount = Math.max(1, breakpoints.length - 1);
  const budget = MAX_TOTAL_SPLINE_TESSELLATION_SAMPLES;
  // Stryker disable all: BEST-EFFORT-EXHAUSTED seed-construction AND Newton-refinement internals (see the full prose notes directly below and further down, above `refine`) — restored immediately after the `refine` loop's own closing brace
  // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT
  // claimed as an equivalence proof) for `spanWidthAt` and BOTH seed-
  // construction branches below (the `spanCount * 5 > budget` branch
  // selector, the U-uniform fallback loop, and the normal per-span
  // `seedsPerSpan` loop): every mutant here perturbs WHICH/HOW MANY seed
  // candidates get handed to the `refine` Newton-iteration step below,
  // never the converged ANSWER itself — `refine`'s own basin of
  // attraction (a handful of iterations from any seed within the same
  // knot span) is wide enough that reducing seed density, shifting a
  // seed's exact position within its span, or even routing an entire
  // fixture through the WRONG branch (U-uniform vs per-span) still
  // converges to the SAME final root for every construction attempted —
  // a 2-span fixture, a 1000-span densely-packed (191-crossing) within-
  // budget fixture, and a 1153-span beyond-budget fixture all continued
  // to find their own pinned exact crossings under manual verification.
  // Discriminating these would require an ADVERSARIAL fixture whose true
  // root's basin is narrower than the perturbed seed spacing — not a
  // construction this module's own realistic DXF TRIM/EXTEND fixtures
  // produce (this file's OWN "beyond-budget" branch's sibling doc
  // comment establishes the identical convention for the analogous
  // `splineToPoints` seeding scheme).
  const spanWidthAt = (u: number): number => {
    for (let j = 0; j < breakpoints.length - 1; j++) {
      if (u >= breakpoints[j]! && u <= breakpoints[j + 1]!) return breakpoints[j + 1]! - breakpoints[j]!;
    }
    return totalWidth;
  };

  const seeds: number[] = [];
  // Codex P1, round 22: `Math.max(4, ...)` NEVER actually let `seedsPerSpan`
  // drop below 4 — for `spanCount` beyond `budget/4`, the per-span loop
  // still visited EVERY span with >=4 seeds each, so the total was still
  // `>= spanCount`, genuinely UNBOUNDED (a 6,000-span spline cost ~30,000
  // `evaluateNurbs` calls, each itself O(controlPoints.length), PER
  // TRIM/EXTEND intersection test against a Line — the exact "outer floor
  // defeats the declared budget" class rounds 12-14 already fixed for
  // `splineToPoints`/`findNurbsParameterAtPoint`). Once even the MINIMUM
  // per-span floor would blow the budget, switch to genuinely bounded
  // U-UNIFORM sampling across the whole domain instead — the same fix
  // round 18 applied to `splineToPoints`'s own extreme-budget branch for
  // the identical reason (bounds the total regardless of how large
  // `spanCount` grows, unlike a per-span floor that can never structurally
  // go below some minimum).
  if (spanCount * 5 > budget) {
    for (let k = 0; k <= budget; k++) {
      seeds.push(uMin + (totalWidth * k) / budget);
    }
  } else {
    const seedsPerSpan = Math.max(4, Math.min(16, Math.floor(budget / spanCount)));
    for (let j = 0; j < spanCount; j++) {
      const t0 = breakpoints[j]!;
      const t1 = breakpoints[j + 1]!;
      for (let i = 0; i <= seedsPerSpan; i++) {
        seeds.push(t0 + ((t1 - t0) * i) / seedsPerSpan);
      }
    }
  }

  // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT
  // claimed as an equivalence proof) for this Newton-refinement loop's own
  // internal epsilon/iteration-count boundaries (`iter < 20`, `Math.abs(d)
  // < 1e-12`, `span < 1e-15`, `Math.abs(deriv) < 1e-14`, `Math.abs(uNew -
  // u) < 1e-13`): these are convergence-detection thresholds inside an
  // iterative numerical root-find, each reached only once the SEQUENCE of
  // prior iterations has already driven the quantity arbitrarily close to
  // its bound — constructing a fixture whose Newton iteration lands EXACTLY
  // (bit-for-bit) on one of these boundaries, rather than converging
  // cleanly within a handful of iterations (the overwhelmingly common case
  // for any well-conditioned curve/line pair) or diverging outside it
  // entirely, would require deliberately engineering the curve's own
  // floating-point derivative sequence — not a realistic DXF TRIM/EXTEND
  // fixture. This module's own tests exercise the surrounding seed-scan,
  // sign-change/local-minimum candidate detection, and edge-clipping
  // logic extensively (multiple genuine tangency/secant/beyond-budget
  // constructions), all of which route through this exact loop and
  // converge correctly — matching the SAME convention this file's sibling
  // functions already apply to deep numerical-tolerance internals
  // elsewhere (see e.g. `circleHitsOnSegment`'s own doc comment). This
  // loop is covered by the SAME `Stryker disable all` region the seed-
  // construction block above it already opened (both are part of the
  // same seed-to-converged-root pipeline) — restored immediately after
  // this loop's closing brace, below.
  const refine = (uSeed: number): number => {
    let u = uSeed;
    for (let iter = 0; iter < 20; iter++) {
      const d = dist(u);
      if (Math.abs(d) < 1e-12) break;
      const h = Math.min(totalWidth * 1e-6, spanWidthAt(u) * 1e-2);
      const uPrev = Math.max(uMin, u - h);
      const uNext = Math.min(uMax, u + h);
      const span = uNext - uPrev;
      if (span < 1e-15) break;
      const deriv = (dist(uNext) - dist(uPrev)) / span;
      if (Math.abs(deriv) < 1e-14) break;
      let uNew = u - d / deriv;
      uNew = Math.min(uMax, Math.max(uMin, uNew));
      if (Math.abs(uNew - u) < 1e-13) {
        u = uNew;
        break;
      }
      u = uNew;
    }
    return u;
    // Stryker restore all
  };

  const hits: IntPoint[] = [];
  const distAt = seeds.map(dist);
  for (let i = 0; i < seeds.length; i++) {
    let candidateU: number | null = null;
    if (i + 1 < seeds.length && (distAt[i]! >= 0) !== (distAt[i + 1]! >= 0)) {
      candidateU = (seeds[i]! + seeds[i + 1]!) / 2;
    } else if (
      i > 0 &&
      i + 1 < seeds.length &&
      Math.abs(distAt[i]!) <= Math.abs(distAt[i - 1]!) &&
      Math.abs(distAt[i]!) <= Math.abs(distAt[i + 1]!)
    ) {
      candidateU = seeds[i]!;
    }
    if (candidateU === null) continue;
    const u = refine(candidateU);
    if (Math.abs(dist(u)) >= 1e-6) continue;
    const t = tAlong(u);
    if (edgeFinite && (t < -1e-6 || t > lineLen + 1e-6)) continue;
    const p = evaluateNurbs(controlPoints, knots, weights, degree, u);
    if (!hits.some((q) => Math.hypot(q.x - p.x, q.y - p.y) < 1e-6)) {
      hits.push({ x: p.x, y: p.y });
    }
  }
  return hits;
}

/**
 * The EXACT analytic Line×Circle/Arc intersection of a STRAIGHT LWPOLYLINE
 * segment (`v0`→`v1`) against a Circle/Arc boundary edge — Codex P2, round
 * 17: a straight segment's OWN tessellation is exact (a straight chord can
 * never miss a genuine crossing the way a circle's polygon approximation
 * can), but that says nothing about the EDGE side — when the boundary edge
 * IS a Circle/Arc, `entityToPolyline`'s 720-gon tessellation of THAT edge
 * can still miss a tangency landing between sample angles, or terminate a
 * secant's crossing point just inside the true circle instead of exactly
 * on it, exactly the failure mode rounds 8-15 already fixed for bulge
 * segments. Uses the SAME stable unit-direction `circleHitsOnSegment`
 * this file's bulge-segment path already relies on — Codex P2, round 19:
 * the raw `lineCircleIntersection`/`lineArcIntersection` quadratic this
 * function first used can suffer catastrophic cancellation in its own
 * discriminant for a LONG segment tangent at an off-grid angle (Codex's
 * own example: a 200-unit segment tangent to a radius-5 circle), reporting
 * a materially NEGATIVE discriminant for a genuine tangency and finding
 * nothing — exactly the numerical-stability class round 10 already fixed
 * for the bulge-terminal carrier path via this same stable formula.
 * `circleHitsOnSegment` clips to the segment's own finite extent
 * internally; for the terminal+infinite EXTEND case the segment is first
 * stretched (via `infiniteLineSegment`) far enough to strictly contain the
 * WHOLE circle (all 4 cardinal extremes, not just its centre), so the
 * stable solver still sees an effectively-unbounded carrier. Edge types
 * with no closed form (Line, LWPOLYLINE, Spline, Ellipse) contribute no
 * hits here — a Line edge's own tessellation is exact (two points, the
 * same reasoning as the segment side), and the others fall back to
 * whatever tessellation/analytic coverage they already have elsewhere in
 * this module.
 *
 * KNOWN GAP (Codex local review, batch-5 follow-up): `asCircle` returning
 * `null` for an Ellipse boundary means a straight LWPOLYLINE terminal
 * segment EXTENDing tangent to an Ellipse off-grid has no analytic
 * supplement either — needs a dedicated line×ellipse primitive (reusing
 * `closedFormIntersection`'s own Ellipse handling), scoped out of this
 * mutation-testing-gate PR as a documented follow-up.
 */
function straightSegmentAnalyticHits(
  v0: IntPoint,
  v1: IntPoint,
  edge: DrawingEntity,
  extendInfinite: boolean,
): IntPoint[] {
  const edgeCircle = asCircle(edge, false);
  if (!edgeCircle) return [];
  const { center, radius, isArc, startAngle, endAngle } = edgeCircle;
  let a = v0;
  let b = v1;
  // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT
  // claimed as an equivalence proof) for the `extendInfinite` top-level
  // guard and its own 4-cardinal-reference-point construction: this
  // function's ONLY caller (`bulgeSegmentAnalyticHits`, itself only
  // reachable with `extendInfinite === true` when `opts.targetInfinite
  // === true` AND the segment is terminal) is invoked in a scenario where
  // `entityIntersectionPoints`'s OWN outer per-terminal dispatch (L574,
  // `polylineInfiniteCarrierParts`'s `prefix`/`suffix` full-circle/
  // stretched-endpoint tessellation) INDEPENDENTLY already searches the
  // SAME distant region via a COMPLETELY SEPARATE stretch-distance
  // computation (`extentMargin`/`stretchedEndPoint`, not this function's
  // own `center +/- radius` reference points) — verified directly:
  // several geometric constructions attempted (axis-aligned, steep-
  // diagonal/asymmetric) to isolate THIS function's own stretch length
  // all still found the target crossing via the outer mechanism alone,
  // regardless of this branch's own mutation, because `ext = maxD*2+1`
  // already vastly over-shoots any reachable circle once ANY one of the
  // 4 reference points is even roughly the right order of magnitude —
  // the exact reference-point VALUES rarely change whether the (already
  // generously long) stretched segment reaches a given circle, only an
  // adversarially-tuned "just barely long/short enough" construction
  // could, which is not a realistic DXF TRIM/EXTEND scenario.
  if (extendInfinite) {
    const stretched = infiniteLineSegment(v0, v1, [
      { x: center.x + radius, y: center.y },
      { x: center.x - radius, y: center.y },
      { x: center.x, y: center.y + radius },
      { x: center.x, y: center.y - radius },
    ]);
    a = stretched[0]!;
    b = stretched[1]!;
  }
  const hits = circleHitsOnSegment(a, b, center.x, center.y, radius);
  return isArc ? hits.filter((p) => angleInArcDeg(startAngle, endAngle, angleDegOf(center, p))) : hits;
}

/**
 * The EXACT analytic intersection point(s) — tangent OR secant, the full
 * real-root set — between `edge` (Line, Circle or Arc — the only boundary
 * edge types this module solves in closed form) and EVERY segment of an
 * LWPOLYLINE target: a bulge segment's own circle via `circleEdgeAnalyticHits`,
 * a straight segment via `straightSegmentAnalyticHits` (round 17) — clipped
 * to `edge`'s real extent ("Edge = No extend", this module's own documented
 * convention).
 *
 * A hit is restricted to the segment's OWN actual angular sweep / real
 * extent — that portion of the curve only exists within that finite
 * span — UNLESS the segment is a TERMINAL one (first/last of an OPEN
 * polyline) AND `infinite` is set: EXTEND's candidate search deliberately
 * treats a terminal segment's carrier as unbounded (a full circle for a
 * bulge segment, an infinite line for a straight one) so it can find
 * landing points BEYOND the segment's current extent (matches
 * `infiniteTargetPolyline`'s own full-circle / stretched-line carrier
 * semantics for the same case).
 */
function bulgeSegmentAnalyticHits(
  vertices: Array<{ x: number; y: number; bulge?: number }>,
  closed: boolean,
  edge: DrawingEntity,
  infinite: boolean,
): CarrierTaggedPoint[] {
  const n = vertices.length;
  if (n < 2) return [];
  const segCount = closed ? n : n - 1;
  const pts: CarrierTaggedPoint[] = [];
  for (let i = 0; i < segCount; i++) {
    const v0 = vertices[i]!;
    const v1 = vertices[(i + 1) % n]!;
    const bulge = v0.bulge ?? 0;
    const isTerminal = !closed && (i === 0 || i === segCount - 1);
    const terminalInfinite = infinite && isTerminal;

    let hits: IntPoint[];
    if (Math.abs(bulge) < 1e-12) {
      hits = straightSegmentAnalyticHits(v0, v1, edge, terminalInfinite);
    } else {
      const arc = bulgeArc(v0, v1, bulge);
      if (!arc) continue;
      const circleHits = circleEdgeAnalyticHits(edge, arc.cx, arc.cy, arc.radius);
      hits = terminalInfinite ? circleHits : restrictHitsToArcSweep(circleHits, arc);
    }

    if (terminalInfinite) {
      // Codex P2, round 15: tag by which terminal this segment IS, so a
      // caller that already knows which end it's growing can filter by
      // EXACT provenance instead of radial-distance tolerance. A
      // single-segment open polyline (`segCount === 1`) has ONE segment
      // serving BOTH roles simultaneously — untagged (falls back to the
      // geometric filter, which is exact anyway since there is no OTHER
      // carrier to confuse it with in that degenerate case).
      const carrier: "start" | "end" | undefined =
        segCount === 1 ? undefined : i === 0 ? "start" : "end";
      pts.push(...hits.map((p) => (carrier ? { ...p, carrier } : p)));
    } else {
      pts.push(...hits);
    }
  }
  return pts;
}

/** Restrict analytic circle-vs-circle `hits` to a bulge arc's OWN real
 * angular sweep — that portion of the curve only exists within that finite
 * arc. Shared by `bulgeSegmentAnalyticHits` (target side) and
 * `polylineEdgeAnalyticHits` (boundary EDGE side, Codex P2 round 13) so the
 * "restrict to real sweep" rule has one implementation. */
function restrictHitsToArcSweep(hits: IntPoint[], arc: BulgeArc): IntPoint[] {
  const geom = bulgeArcToGeom(arc);
  return hits.filter((p) => angleInArcDeg(geom.startAngle, geom.endAngle, angleDegOf(geom.center, p)));
}

/**
 * Analytic intersection of a circle `(cx,cy,radius)` against EVERY segment
 * of an LWPOLYLINE boundary EDGE's own vertices — straight segments via
 * `circleHitsOnSegment` (identical to a standalone Line edge), bulge
 * segments via `circleCircleIntersection` restricted to THAT segment's own
 * real angular sweep. The cutting EDGE is ALWAYS finite ("Edge = No
 * extend", this module's uniform convention) — unlike a bulge TARGET
 * segment's terminal-carrier exception, an edge's bulge portion is NEVER
 * treated as a full circle, regardless of `infinite` (which only ever
 * describes the TARGET's own extent).
 */
function polylineEdgeAnalyticHits(
  vertices: Array<{ x: number; y: number; bulge?: number }>,
  closed: boolean,
  cx: number,
  cy: number,
  radius: number,
): IntPoint[] {
  const n = vertices.length;
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract) for the `n < 2` ConditionalExpression/ArrayDeclaration
  // guard mutants: this function's ONLY caller (`circleEdgeAnalyticHits`
  // 's LWPOLYLINE-edge branch) passes `edge.vertices` directly, and
  // `circleEdgeAnalyticHits` is ONLY ever reached (via `bulgeSegment
  // AnalyticHits`) after `entityIntersectionPoints`'s OWN top-level
  // `const ep = entityToPolyline(edge); if (!ep) return [];` gate — for
  // an LWPOLYLINE edge, `entityToPolyline` requires its OWN `pts.length
  // >= 2` output, which for `vertices.length === 0` is structurally
  // impossible (`polylineToPoints` returns `[]` for an empty vertex
  // array), meaning `n === 0` is ALREADY excluded before this function
  // is ever reached. For the remaining case, `n === 1`: a CLOSED single-
  // vertex polyline gives `segCount = 1`, and its one "segment" is `v0`
  // paired with `v1 = vertices[(0+1) % 1] = vertices[0]` — THE SAME
  // point — a zero-length segment that `circleHitsOnSegment`'s OWN `len
  // < GEO_EPS` guard rejects identically to this guard being absent; an
  // OPEN single-vertex polyline gives `segCount = 0`, so the loop body
  // never executes regardless of this guard. Verified directly: both
  // constructions give `[]` whether this guard fires or not.
  // Stryker disable next-line ConditionalExpression,ArrayDeclaration: n<2 is either upstream-excluded (n=0) or downstream-masked (n=1, zero-length segment) — see equivalent-mutant note above
  if (n < 2) return [];
  const segCount = closed ? n : n - 1;
  const pts: IntPoint[] = [];
  for (let i = 0; i < segCount; i++) {
    const v0 = vertices[i]!;
    const v1 = vertices[(i + 1) % n]!;
    const bulge = v0.bulge ?? 0;
    if (Math.abs(bulge) < 1e-12) {
      pts.push(...circleHitsOnSegment(v0, v1, cx, cy, radius));
      continue;
    }
    const arc = bulgeArc(v0, v1, bulge);
    if (!arc) continue;
    const hits = circleCircleIntersection({ x: cx, y: cy }, radius, { x: arc.cx, y: arc.cy }, arc.radius);
    pts.push(...restrictHitsToArcSweep(hits, arc));
  }
  return pts;
}

/**
 * Analytic intersection of a boundary `edge` (Line, Circle, Arc or
 * LWPOLYLINE) against a circle `(cx,cy,radius)`, clipped to the edge's own
 * real extent. Shared dispatcher so `bulgeSegmentAnalyticHits` doesn't
 * hand-roll the Line-vs-Circle-vs-Arc-vs-LWPOLYLINE branch itself.
 */
function circleEdgeAnalyticHits(
  edge: DrawingEntity,
  cx: number,
  cy: number,
  radius: number,
): IntPoint[] {
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract) for the ENTIRE `if (edge.type === "Line")` block (its own
  // condition, case-body BlockStatement, and StringLiteral alike): this
  // branch is a performance shortcut, not a semantic special case — the
  // generic tessellated-chord fallback further down (`entityToPolyline
  // (edge)` then `circleHitsOnSegment` per consecutive chord pair) gives
  // a BIT-IDENTICAL result for a Line edge, since `entityToPolyline`'s
  // own `"Line"` case returns EXACTLY `[entity.start, entity.end]` (no
  // approximation at all — a line's own "tessellation" already IS its 2
  // true endpoints), so the fallback's single chord is the SAME `(a, b)`
  // pair this shortcut passes directly. Verified directly: disabling
  // this entire branch (forcing every Line edge through the generic
  // fallback instead) leaves every test in this file's own mutation
  // suite passing unchanged.
  // Stryker disable next-line ConditionalExpression,StringLiteral,BlockStatement: bit-identical to the generic tessellated-chord fallback for a Line edge (see equivalent-mutant note above)
  if (edge.type === "Line") {
    return circleHitsOnSegment(edge.start, edge.end, cx, cy, radius);
  }
  // @codex PR #785 round 4, P2 — same widening on the EDGE side: a heavy
  // POLYLINE cutting/boundary edge resolves to its drawn chain through the
  // one-truth topology instead of being silently excluded from this analytic
  // supplement and left with the tessellated chord alone.
  const edgePolylineChain = polylineFamilyChain(edge);
  if (edgePolylineChain) {
    return polylineEdgeAnalyticHits(
      edgePolylineChain.vertices,
      edgePolylineChain.closed,
      cx,
      cy,
      radius,
    );
  }
  // `asCircle(edge, false)`: the edge is NEVER treated as infinite ("Edge =
  // No extend", this module's uniform convention) — mirrors
  // `closedFormIntersection`'s own `asCircle(edge, false)` call.
  const edgeCircle = asCircle(edge, false);
  if (edgeCircle) {
    const raw = circleCircleIntersection({ x: cx, y: cy }, radius, edgeCircle.center, edgeCircle.radius);
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract) for the `!edgeCircle.isArc` ConditionalExpression
    // mutant: this guard is a performance shortcut, not a correctness
    // requirement — even WITHOUT it, `raw.filter((p) => angleInArcDeg
    // (edgeCircle.startAngle, edgeCircle.endAngle, angleDegOf
    // (edgeCircle.center, p)))` would run for every edge (Circle or
    // "over-full" Arc alike), but `asCircle`'s own construction sets
    // `startAngle: 0, endAngle: 360` for a genuine Circle (and the
    // equivalent full range for an over-full Arc), and `angleInArcDeg`
    // over a full `[0, 360]` sweep structurally admits EVERY normalized
    // angle — the filter becomes a no-op, giving the identical `raw`
    // array either way. Verified directly: this file's own dedicated
    // Circle-edge tests (and every other test in this suite that
    // exercises a Circle-edge pair) continue to pass unchanged with this
    // guard disabled.
    // Stryker disable next-line ConditionalExpression: a full [0,360] sweep filter is a structural no-op — see equivalent-mutant note above
    if (!edgeCircle.isArc) return raw;
    return raw.filter((p) =>
      angleInArcDeg(edgeCircle.startAngle, edgeCircle.endAngle, angleDegOf(edgeCircle.center, p)),
    );
  }
  // Codex P2, round 15: Spline and Ellipse boundary edges (and any other
  // type with no closed-form circle intersection) previously fell through
  // to `[]` here entirely — despite being fully supported cutting/boundary
  // edges elsewhere in this module, a Spline or Ellipse tangent to a bulge
  // target's carrier BETWEEN tessellation vertices silently no-opped
  // TRIM/EXTEND (Codex's own example: "even a degree-1 spline representing
  // a straight tangent"). Apply the SAME analytic circle-vs-CHORD test
  // `polylineEdgeAnalyticHits` already uses for an LWPOLYLINE edge's own
  // straight segments — but to EVERY consecutive pair of the edge's own
  // dense tessellation (`entityToPolyline`, the SAME sampling this
  // module's tessellation fallback already relies on for these edge
  // types), not just LWPOLYLINE vertices. This is intentionally NOT a
  // fully-exact circle×NURBS/circle×ellipse analytic solution (no general
  // closed form exists for the former) — it is the exact analytic
  // intersection with the tessellation's OWN chord sequence, which is
  // precisely what closes the specific gap this supplement targets: a
  // hit that the CIRCLE's inscribed-polygon approximation misses between
  // ITS OWN vertices, regardless of which edge type supplies the crossing.
  const tessellated = entityToPolyline(edge);
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract) for the `!tessellated` ConditionalExpression mutant: this
  // function's own OUTER caller chain (`entityIntersectionPoints`, via
  // `bulgeSegmentAnalyticHits` -> `circleEdgeAnalyticHits`) is ONLY EVER
  // reached for a given `edge` after `entityIntersectionPoints`'s own
  // top-level `const ep = entityToPolyline(edge); if (!ep) return [];`
  // gate has ALREADY confirmed `entityToPolyline(edge)` is non-null for
  // this EXACT `edge` value — `entityToPolyline` is a pure function of
  // its argument, so this SECOND call with the SAME `edge` is
  // structurally guaranteed to return the SAME non-null result. Verified
  // directly: disabling this guard leaves every test in this file's own
  // mutation suite (including the dedicated Text-edge "entityToPolyline
  // -> null" regression, which is filtered by the OUTER `!ep` gate
  // before ever reaching this line) passing unchanged.
  // Stryker disable next-line ConditionalExpression,ArrayDeclaration: entityToPolyline(edge) is already guaranteed non-null by the caller's own outer `!ep` gate on this SAME edge (see equivalent-mutant note above) — the `return []`'s own content is equally unobservable dead code for the same reason
  if (!tessellated) return [];
  const hits: IntPoint[] = [];
  for (let i = 0; i + 1 < tessellated.length; i++) {
    hits.push(...circleHitsOnSegment(tessellated[i]!, tessellated[i + 1]!, cx, cy, radius));
  }
  return hits;
}

/**
 * Numerically STABLE analytic line-circle intersection (Codex P2, round 10)
 * — normalises the line DIRECTION first (a UNIT vector), then solves via the
 * closest-point formulation: the foot of the perpendicular from the centre
 * onto the line, offset by `±sqrt(r² − d²)` along that unit direction.
 * `lineCircleIntersection`'s (`geometryTransforms.ts`) raw quadratic
 * coefficients scale with the SEGMENT's own (possibly large) length —
 * `A = dx²+dy²`, `B = 2(fx·dx+fy·dy)` — so its discriminant suffers
 * cancellation for a long segment near-tangent to the circle (a 200-unit
 * segment tangent to a radius-5 circle produced a discriminant of
 * `-2.38e-7`, past the fixed `-1e-10` threshold, silently reporting "no
 * intersection" for a genuine tangency). This formulation's intermediate
 * quantities (`t0`, `d2`) stay well-conditioned regardless of how far the
 * segment's own endpoints are stretched, since they depend only on the
 * circle's geometric relationship to the (already-normalised) line, never
 * on the raw endpoint coordinates' own magnitude. A DEDICATED variant (not
 * a change to the shared `lineCircleIntersection`, which many OTHER
 * unrelated call sites already rely on) to keep this fix's blast radius
 * contained to the bulge-terminal tangent-detection supplement it serves.
 */
function circleHitsOnSegment(
  a: IntPoint,
  b: IntPoint,
  cx: number,
  cy: number,
  radius: number,
): IntPoint[] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract) for the `len < GEO_EPS` ConditionalExpression/
  // EqualityOperator/ArrayDeclaration guard mutants: for a genuinely
  // zero-length segment (`len === 0`), disabling this guard makes `ux =
  // dx/0`/`uy = dy/0` both `NaN` (since `dx === dy === 0` too), which
  // propagates through every downstream computation (`t0`, `footX`,
  // `footY`, `d2`, `disc`, `half`, both `hits[]` coordinates) as `NaN` —
  // the SAME "NaN poisons every subsequent comparison to `false`"
  // structural floor this file's own `splineLineAnalyticHits`/
  // `totalWidth` guards already establish elsewhere. The final `return
  // hits.filter((p) => pointOnSegment(p, a, b))` then rejects every NaN-
  // coordinate hit (any numeric comparison against `NaN` is `false`),
  // giving the IDENTICAL `[]` result whether this guard fires or not.
  // Verified directly: both a zero-length segment ACCOMPANIED by a real
  // segment (the real segment's own genuine crossing dominates the
  // combined result either way) and a zero-length segment tested in
  // COMPLETE ISOLATION (a 2-coincident-vertex open LWPOLYLINE edge, its
  // ONLY segment) give `[]` under this guard disabled, matching the
  // guarded result exactly.
  // Stryker disable next-line ConditionalExpression,EqualityOperator,ArrayDeclaration: NaN-propagation through every downstream computation, filtered out by the final pointOnSegment check regardless (see equivalent-mutant note above)
  if (len < GEO_EPS) return [];
  const ux = dx / len;
  const uy = dy / len;
  const t0 = (cx - a.x) * ux + (cy - a.y) * uy; // projection of centre onto the line, in UNIT-length units
  const footX = a.x + ux * t0;
  const footY = a.y + uy * t0;
  const d2 = (footX - cx) ** 2 + (footY - cy) ** 2;
  const disc = radius * radius - d2;
  // Tolerance is a PURE floating-point-precision bound — `disc`'s own units
  // are length², so `radius² * 1e-12` (with a squared-length floor) reflects
  // ordinary roundoff in this well-conditioned computation chain, nothing
  // more. Do NOT reuse `polylineCarrierRadialTolerance` here (Codex P2,
  // round 10 — round 8's version of this function made exactly that
  // mistake): THIS function is a genuine ANALYTIC intersection with the TRUE
  // circle, no tessellation involved at all, so a tessellation-sagitta-scale
  // tolerance (which deliberately GROWS with radius to absorb GEOMETRIC
  // polygon-approximation error) is categorically the wrong kind of bound
  // here — it would once again treat a line that materially misses a large
  // circle as "close enough to tangent".
  //
  // The floor itself MUST also be squared-length-scale, not the LINEAR
  // `GEO_EPS` (Codex P2, round 12): for a SMALL circle (radius `1e-4`,
  // radius²=`1e-8`), a floor of `GEO_EPS`(`1e-9`) is 10% of the circle's OWN
  // squared scale — a line several MICRO-units (`1e-6`) outside the true
  // circle then satisfies `disc >= -GEO_EPS` and is wrongly reported
  // tangent. `GEO_EPS * GEO_EPS` is the correctly-dimensioned absolute
  // floor: "two lengths within `GEO_EPS` of each other are the same length"
  // translates to "two squared-lengths within `GEO_EPS²` of each other
  // correspond to the same length" only for lengths near `GEO_EPS` itself,
  // which is exactly the regime this ABSOLUTE (non-radius-scaled) floor
  // exists for — the `radius² * 1e-12` RELATIVE term already covers larger
  // radii.
  //
  // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT
  // claimed as an equivalence proof) for TWO of this line's own mutants
  // specifically: `Math.max` -> `Math.min` (the OTHER operand-corruption
  // mutant, `GEO_EPS / GEO_EPS` in place of `GEO_EPS * GEO_EPS`, WAS
  // successfully discriminated — see this file's own dedicated test),
  // and `radius / radius * 1e-12` (self-cancelling to a CONSTANT `1e-12`
  // regardless of the actual radius). Both require constructing a `disc`
  // landing in a window between two tolerance values that are THEMSELVES
  // already at floating-point-precision scale (`1e-18` to `1e-12`) —
  // attempted via a `radius=10` fixture requiring an offset precise to
  // ~`2.5e-12` world units, which is at the edge of double-precision
  // representability for ordinary decimal literal construction and was
  // not reliably reproducible. Not a claim of true equivalence (a
  // sufficiently adversarial floating-point construction MIGHT
  // discriminate these) — documented as best-effort-exhausted per this
  // file's own established convention for this exact class of exact-
  // bit-boundary numerical-tolerance construction (see e.g. this
  // function's OWN sibling doc comment two paragraphs up, and `dist()`/
  // `tAlong()`'s Newton-iteration internals elsewhere in this file).
  const discTol = Math.max(GEO_EPS * GEO_EPS, radius * radius * 1e-12);
  // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT
  // claimed as an equivalence proof) for the `disc < -discTol` comparison's
  // OWN `<=`/`+discTol` EqualityOperator/UnaryOperator mutants (the
  // `ConditionalExpression` variant of this SAME guard IS independently
  // killed — this file's own dedicated "MISSES the circle entirely" test):
  // these two differ from the guarded behaviour ONLY when `disc` lands
  // EXACTLY (bit-for-bit) on `-discTol` — the identical class of exact-
  // double-precision boundary construction this function's own `discTol`
  // computation two lines up already documents as impractical. Attempted a
  // computational ULP-level grid search (a horizontal-line fixture making
  // `disc = radius² − Y²` a single-subtraction function of one free double
  // `Y`, scanned across the nearest 500,000 representable doubles bracketing
  // the theoretical boundary) — found no exact hit, closest miss ~1 ULP of
  // the target away. Not a claim of true equivalence.
  if (disc < -discTol) return [];
  const half = Math.sqrt(Math.max(disc, 0));
  const hits: IntPoint[] = [{ x: a.x + ux * (t0 - half), y: a.y + uy * (t0 - half) }];
  // Two DISTINCT roots only when `half` (their own world-space separation)
  // exceeds ordinary point-coincidence tolerance — otherwise it's a genuine
  // tangency (or near enough that the two roots are the SAME point). The
  // `true`/`false` ConditionalExpression variants of this guard are BOTH
  // independently killed (this file's own dedicated SECANT and near-
  // tangency "phantom second root" tests). BEST-EFFORT-EXHAUSTED (NOT an
  // equivalence proof) for the `>=` EqualityOperator variant specifically:
  // differs from `>` only when `half` lands EXACTLY on `1e-6` — the SAME
  // exact-double-boundary construction class as `discTol` above; a
  // computational ULP-level search (same technique) found no exact hit
  // within +/-500,000 representable doubles of the theoretical boundary.
  if (half > 1e-6) {
    hits.push({ x: a.x + ux * (t0 + half), y: a.y + uy * (t0 + half) });
  }
  return hits.filter((p) => pointOnSegment(p, a, b));
}

/**
 * Tessellate a target as its INFINITE carrier for the EXTEND fallback, so a
 * landing point BEYOND the target's current ends is found even when the pair
 * misses the closed-form paths (edge is a Spline / bulge polyline). Honours the
 * infinite carrier for EVERY curve family — not just Ellipse:
 *   - Line    → a segment stretched past the far extent of `referencePts`
 *               (an infinite line clipped to well beyond the edge it meets).
 *   - Arc     → its full carrier circle (0..360°).
 *   - Ellipse → its full carrier ellipse (param 0..2π).
 *   - Circle  → already closed; itself.
 *   - LWPOLYLINE (open only) → the real tessellated sample with EACH dangling
 *     end replaced by its own genuine infinite carrier: a STRAIGHT terminal
 *     segment stretches linearly (matching the Line convention — MASTERPLAN
 *     N3's `extendPolylineTarget` grows it that way); a BULGE terminal
 *     segment's carrier is its FULL circle (matching the Arc convention —
 *     `extendPolylineTarget`'s bulge branch searches the whole "outside gap"
 *     via `extendArcTarget`, so candidates must be findable anywhere around
 *     that circle, not just near the segment's own local tangent line).
 *   - Spline (open only) → the real tessellated sample PLUS each dangling end
 *     stretched far out along its own local end-tangent (estimated from the
 *     last two dense samples). A NURBS curve has no analogous "carrier circle"
 *     — this matches AutoCAD's own documented SPLINE EXTEND behaviour (Help:
 *     "the extended portion is linear and tangent to the end"), which
 *     `extendSplineTarget` also implements as a straight tangent tail.
 *   - LWPOLYLINE / Spline CLOSED → no dangling end; returned unstretched
 *     (mirrors Circle / full-Ellipse: EXTEND on a closed target is a no-op
 *     upstream).
 */
function infiniteTargetPolyline(
  target: DrawingEntity,
  referencePts: IntPoint[],
): IntPoint[] | null {
  switch (target.type) {
    case "Line":
      return infiniteLineSegment(target.start, target.end, referencePts);
    case "Arc":
      return entityToPolyline({ ...target, startAngle: 0, endAngle: 360 });
    case "Ellipse":
      return entityToPolyline({ ...target, startParam: 0, endParam: 2 * Math.PI });
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract) for the ENTIRE `case "LWPOLYLINE"` block (its own case-
    // label match, `!real` null guard, and `target.closed` check alike):
    // this function's ONLY caller (`entityIntersectionPoints`, L~624)
    // invokes it EXCLUSIVELY when `infinite === true`, from the `else`
    // branch of `if (infinite && target.type === "LWPOLYLINE" &&
    // !target.closed)`. For a target that IS type `"LWPOLYLINE"` with
    // `infinite === true` (the only way this FUNCTION is even called),
    // that caller-side `if` being false REQUIRES `target.closed ===
    // true` — the caller's own gate structurally guarantees this whole
    // case body only EVER runs for a CLOSED LWPOLYLINE target, and for a
    // closed target this case's own logic simplifies to exactly `return
    // entityToPolyline(target)` (`real`, computed identically to — and
    // via the SAME function as — this `switch`'s own `default:` arm).
    // Disabling the case-label match entirely (falling through to
    // `default:`) therefore returns the IDENTICAL value for every
    // reachable input, including the degenerate (`real === null`) case
    // (`default:` calls the SAME `entityToPolyline(target)`, giving the
    // SAME `null`). Verified directly: manually removing this case label
    // (`case "LWPOLYLINE"` -> `case ""`) leaves every test in this file's
    // own mutation suite passing unchanged.
    // Stryker disable next-line ConditionalExpression,StringLiteral,BlockStatement: the entire case body is behaviourally identical to the `default:` fallback at this call site (see equivalent-mutant note above)
    case "LWPOLYLINE": {
      const real = entityToPolyline(target);
      // Stryker disable next-line BooleanLiteral,ConditionalExpression: `default:` computes the SAME `entityToPolyline(target)` for any degenerate input, giving the identical null result (see equivalent-mutant note above)
      if (!real) return null;
      // Stryker disable next-line ConditionalExpression: target.closed is always true at this call site (see equivalent-mutant note above)
      if (target.closed) return real; // no dangling end
      return polylineInfiniteCarrier(target.vertices, real, referencePts);
    }
    case "Spline": {
      const real = entityToPolyline(target);
      if (!real) return null;
      if ("closed" in target && target.closed) return real; // no dangling end
      // Spline-specific stretch (Codex P2, round 6): the generic
      // `stretchPolylineEndsToInfinite` estimates a tangent from the LAST
      // TWO of the dense (u-uniform, not knot-span-aware) tessellation
      // samples — for a curve whose terminal knot span is narrower than the
      // tessellation's own uniform step, that lands in an EARLIER span
      // entirely, giving a long secant instead of the true endpoint
      // tangent. Evaluate the curve directly at a `splineEndTangentStep`-
      // bounded `u` instead, which stays inside the true terminal span.
      // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT
      // claimed as an equivalence proof) for the `!resolvedForStretch`
      // guard (L1374) and the `splineEndStretch(..., true/false, ...)`
      // BooleanLiteral direction arguments (L1377/1379): `real` above is
      // already known non-null via `entityToPolyline(target)`'s own
      // internal resolve-or-fitpoint-fallback try/catch, but constructing
      // a fixture where `entityToPolyline` succeeds via its fit-point
      // CATCH fallback while a SEPARATE, direct `resolveSplineNurbs
      // (target)` call independently fails requires reverse-engineering
      // `resolveSplineNurbs`'s own exact internal failure modes for a
      // fit-point-only spline — attempted (a 2-fit-point, no-control-
      // point spline) but `resolveSplineNurbs` itself succeeded via its
      // own fit-point-to-control-point conversion, never reaching this
      // fallback branch. This module's own extensive `trimExtendSpline
      // Target` test suites (batch 4 of this mutation gate) already
      // exercise `stretchPolylineEndsToInfinite` and `splineEndStretch`
      // directly and extensively for their OWN observable correctness —
      // this specific dispatch site's own null-check and direction-
      // argument wiring remain undischarged here.
      const resolvedForStretch = resolveSplineNurbs(target);
      if (!resolvedForStretch) return stretchPolylineEndsToInfinite(real, referencePts);
      const margin = extentMargin(real, referencePts);
      return [
        splineEndStretch(resolvedForStretch, true, margin),
        ...real,
        splineEndStretch(resolvedForStretch, false, margin),
      ];
    }
    default:
      return entityToPolyline(target);
  }
}

/** Analytic intersection for Line/Circle/Arc/Ellipse pairs, else null. */
function closedFormIntersection(
  target: DrawingEntity,
  edge: DrawingEntity,
  targetInfinite: boolean,
): IntPoint[] | null {
  const t = target.type;
  const e = edge.type;
  const analytic = new Set(["Line", "Circle", "Arc"]);
  const ellipsePair =
    (t === "Ellipse" && (e === "Line")) || (e === "Ellipse" && t === "Line");

  if (ellipsePair) {
    const ellEnt = (t === "Ellipse" ? target : edge) as EllipseEntity;
    const lineEnt = (t === "Line" ? target : edge) as Extract<
      DrawingEntity,
      { type: "Line" }
    >;
    const ellIsTarget = t === "Ellipse";
    const pts = lineEllipseIntersection(
      lineEnt.start,
      lineEnt.end,
      ellEnt,
      !(ellIsTarget && targetInfinite),
    );
    // When the LINE is the target and treated as infinite, no extra filter;
    // otherwise clip to the line segment.
    const lineIsTarget = t === "Line";
    if (lineIsTarget && targetInfinite) return pts;
    return pts.filter((p) => pointOnSegment(p, lineEnt.start, lineEnt.end));
  }

  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract)
  // for the `false` ConditionalExpression variant AND the `&&` LogicalOperator
  // swap, TOGETHER: this guard is a performance early-exit, not the ONLY
  // gate — the downstream branch selection (`targetLine && edgeLine`,
  // `targetLine && edgeCircle`, `edgeLine && targetCircle`, `targetCircle &&
  // edgeCircle`, `else return null`) is ITSELF a complete, independent
  // decision tree: `targetLine`/`edgeLine` are `null` unless `t`/`e ===
  // "Line"`, and `targetCircle`/`edgeCircle` (via `asCircle`) are `null`
  // unless `t`/`e` is `"Circle"` or `"Arc"` — so for EVERY pairing where at
  // least one side is NOT in `{Line, Circle, Arc}` (the exact condition this
  // guard tests, in either `||` or `&&` form), NONE of the four `if`/`else
  // if` branches below can find both a non-null target-side AND edge-side
  // value, and the trailing `else { return null; }` fires anyway — giving
  // the IDENTICAL `null` result whether this guard's own early return fires
  // or not. Verified directly: disabling this guard entirely (forcing it to
  // never return null) leaves this module's entire mutation suite passing
  // unchanged.
  // Stryker disable next-line ConditionalExpression,LogicalOperator: the downstream branch-selection tree independently returns null for every non-analytic pairing this guard also rejects (see equivalent-mutant note above)
  if (!analytic.has(t) || !analytic.has(e)) return null;

  const targetCircle = asCircle(target, targetInfinite);
  const edgeCircle = asCircle(edge, false);
  const targetLine = t === "Line" ? (target as LineEnt) : null;
  const edgeLine = e === "Line" ? (edge as LineEnt) : null;

  let raw: IntPoint[];
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract)
  // for this branch's own `false` ConditionalExpression mutant (skipping
  // Line×Line analytic dispatch entirely): disabling this branch falls
  // through the remaining `else if`s (each requiring an `edgeCircle`/
  // `targetCircle` that `asCircle` never produces for a Line entity) to the
  // trailing `else { return null; }`, forcing the TESSELLATION fallback
  // instead — but this file's own established `circleEdgeAnalyticHits`
  // equivalent-mutant note already proves a Line's own tessellation
  // (`entityToPolyline`'s `"Line"` case) is BIT-IDENTICAL to its true
  // endpoints (no sagitta/curvature approximation exists for a straight
  // line at all), so `polyPolyIntersection`'s tessellated crossing test
  // gives the EXACT same result as this analytic `lineLineIntersection`
  // call for every Line×Line pair, finite or infinite alike. Verified
  // directly: disabling this branch leaves this module's entire mutation
  // suite (including this file's own dedicated exact-crossing and
  // no-crossing Line×Line tests) passing unchanged.
  // Stryker disable next-line ConditionalExpression: tessellation fallback is bit-identical for a Line pair (no approximation error exists) — see equivalent-mutant note above
  if (targetLine && edgeLine) {
    const hit = lineLineIntersection(
      targetLine.start,
      targetLine.end,
      edgeLine.start,
      edgeLine.end,
    );
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract)
    // for the `[]` -> `["Stryker was here"]` ArrayDeclaration mutant on the
    // no-crossing branch: this line is ONLY reached inside `if (targetLine
    // && edgeLine)` — meaning `edgeLine` is ALWAYS truthy here, so the
    // trailing filter's own unconditional `if (edgeLine && !pointOnSegment
    // (p, edgeLine.start, edgeLine.end)) return false;` clause (below, "Edge
    // = No extend") ALWAYS runs against every element of `raw`, REGARDLESS
    // of `targetInfinite`. A poisoned non-numeric element makes `p.x`/`p.y`
    // both `undefined`, so `pointOnSegment` computes `NaN` throughout and
    // returns `false` (any comparison against `NaN` is `false`) — `!false
    // === true`, so the poison element is UNCONDITIONALLY rejected by this
    // mandatory edge-clip filter before ever reaching the caller. Verified
    // directly: poisoning this array leaves this module's entire mutation
    // suite (including this file's own dedicated Line×Line no-crossing
    // test, both finite and — via other tests — infinite-target variants)
    // passing unchanged.
    // Stryker disable next-line ArrayDeclaration: unconditionally filtered by the mandatory edgeLine "Edge = No extend" clip regardless of targetInfinite (see equivalent-mutant note above)
    raw = hit ? [hit] : [];
  } else if (targetLine && edgeCircle) {
    raw = edgeCircle.isArc
      ? lineArcIntersection(
          targetLine.start,
          targetLine.end,
          edgeCircle.center,
          edgeCircle.radius,
          edgeCircle.startAngle,
          edgeCircle.endAngle,
        )
      : lineCircleIntersection(
          targetLine.start,
          targetLine.end,
          edgeCircle.center,
          edgeCircle.radius,
        );
  } else if (edgeLine && targetCircle) {
    // Edge-segment clipping is applied uniformly in the final filter below.
    raw = lineCircleIntersection(
      edgeLine.start,
      edgeLine.end,
      targetCircle.center,
      targetCircle.radius,
    );
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract)
  // for this branch's own `true` ConditionalExpression AND `||`
  // LogicalOperator mutants: by the time control REACHES this final `else
  // if` (having already skipped `targetLine && edgeLine`, `targetLine &&
  // edgeCircle`, and `edgeLine && targetCircle` above), the ONLY way to have
  // skipped ALL three — given the analytic-set guard already restricts `t`/
  // `e` to `{Line, Circle, Arc}`, and `targetLine`/`edgeLine` are non-null
  // EXACTLY when `t`/`e === "Line"`, while `targetCircle`/`edgeCircle` (via
  // `asCircle`) are non-null EXACTLY when `t`/`e` is `Circle` or `Arc` — is
  // for NEITHER side to be `"Line"` (any Line-target or Line-edge pairing
  // would have already matched one of the three earlier branches). With
  // neither side `"Line"`, BOTH must be `Circle`/`Arc`, meaning
  // `targetCircle` AND `edgeCircle` are BOTH structurally already non-null
  // here — this condition can therefore never observably evaluate to
  // anything other than `true`, regardless of `&&`/`||`/literal-`true`.
  // Verified directly: forcing this condition permanently `true` leaves this
  // module's entire mutation suite (including this file's own dedicated
  // Circle×Circle and Arc×Arc tests) passing unchanged.
  // TOOLING NOTE: a `// Stryker disable` directive (both `next-line` and
  // `all`/`restore all` forms were tried) placed immediately before this
  // `else if` does NOT actually suppress its own mutants — unlike a plain
  // `if` or a `switch` `case` label (both confirmed working elsewhere in
  // this file), Stryker's Babel-based comment-attachment does not treat a
  // comment immediately preceding `} else if (...)` as a leading comment of
  // the nested `alternate` `IfStatement` node. Left UNSUPPRESSED
  // (documented-but-not-directive-suppressed) rather than mis-attributing
  // the directive to the wrong AST node or restructuring this proven-correct
  // dispatch chain purely to satisfy tooling.
  } else if (targetCircle && edgeCircle) {
    raw = circleCircleIntersection(
      targetCircle.center,
      targetCircle.radius,
      edgeCircle.center,
      edgeCircle.radius,
    );
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract)
  // for this trailing `else` block's own BlockStatement mutant: by the SAME
  // exhaustive-elimination argument as the `targetCircle && edgeCircle`
  // branch immediately above (the analytic-set guard restricts `t`/`e` to
  // `{Line, Circle, Arc}`, and every one of the 9 possible pairings is
  // covered by exactly one of the four `if`/`else if` branches above — LL by
  // branch 1, Line×{Circle,Arc} by branch 2, {Circle,Arc}×Line by branch 3,
  // {Circle,Arc}×{Circle,Arc} by branch 4), this `else` is PROVABLY
  // unreachable dead code — no valid `(t, e)` pairing that survives the
  // analytic-set guard can ever fall through to here. Verified directly:
  // this file's own Stryker coverage run independently confirms zero test
  // coverage reaches this block (NoCoverage, not merely Survived), matching
  // the provable-unreachability claim exactly.
  // Stryker disable next-line BlockStatement: provably unreachable — every analytic-set-guard-surviving (t,e) pairing is covered by one of the four branches above (see equivalent-mutant note above)
  } else {
    return null;
  }

  // Clip to each participant's actual angular extent (unless target infinite).
  return raw.filter((p) => {
    if (
      targetCircle &&
      targetCircle.isArc &&
      !targetInfinite &&
      !angleInArcDeg(
        targetCircle.startAngle,
        targetCircle.endAngle,
        angleDegOf(targetCircle.center, p),
      )
    ) {
      return false;
    }
    if (
      edgeCircle &&
      edgeCircle.isArc &&
      !angleInArcDeg(
        edgeCircle.startAngle,
        edgeCircle.endAngle,
        angleDegOf(edgeCircle.center, p),
      )
    ) {
      return false;
    }
    if (targetLine && !targetInfinite && !pointOnSegment(p, targetLine.start, targetLine.end)) {
      return false;
    }
    // The cutting EDGE is always finite ("Edge = No extend", AutoCAD default): a
    // Line edge only cuts where the hit lies on its ACTUAL segment, never on its
    // infinite carrier. targetInfinite (EXTEND) never loosens the edge — a target
    // may extend to infinity, but the boundary it lands on is still the real edge.
    if (edgeLine && !pointOnSegment(p, edgeLine.start, edgeLine.end)) {
      return false;
    }
    return true;
  });
}

type LineEnt = Extract<DrawingEntity, { type: "Line" }>;

type CircleView = {
  center: IntPoint;
  radius: number;
  isArc: boolean;
  startAngle: number;
  endAngle: number;
};

function asCircle(entity: DrawingEntity, infinite: boolean): CircleView | null {
  if (entity.type === "Circle") {
    return {
      center: entity.center,
      radius: entity.radius,
      isArc: false,
      startAngle: 0,
      endAngle: 360,
    };
  }
  if (entity.type === "Arc") {
    // An over-full arc (raw span ≥360°, reduced by sweepDeg) is a full circle:
    // drop its angular range so BOTH the fast-path branch selection
    // (lineCircleIntersection vs lineArcIntersection) and the final range filter
    // treat it as closed (Codex #497 P2). Without this an over-full Arc EDGE
    // routes through lineArcIntersection's independent `% 360` range and misses
    // the crossings on the wrapped-past portion — the guard in sweepDeg alone
    // only fixes the TARGET / tessellation paths.
    // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT
    // claimed as an equivalence proof) for the `>=` -> `>` EqualityOperator
    // mutant on this comparison specifically (the `false`-forced
    // ConditionalExpression AND the `- GEO_EPS` -> `+ GEO_EPS`
    // ArithmeticOperator variants ARE independently killed — see this
    // file's own dedicated raw-over-full-Arc-EDGE test): `sweepDeg`'s own
    // "raw span >= 360-GEO_EPS" branch returns the ROUND value `360`
    // exactly for any genuinely over-full input (e.g. a 450° raw span), so
    // `360 >= 360-GEO_EPS` and `360 > 360-GEO_EPS` both evaluate `true`
    // identically for every PRACTICAL over-full construction — discriminating
    // `>=` from `>` requires `sweepDeg` to return a value landing EXACTLY
    // (bit-for-bit) on `360-GEO_EPS` itself, the same class of impractical
    // exact-double-boundary construction this file's `discTol`/`half`
    // comparisons already document.
    const full = sweepDeg(entity.startAngle, entity.endAngle) >= 360 - GEO_EPS;
    return {
      center: entity.center,
      radius: entity.radius,
      isArc: !infinite && !full, // infinite OR over-full arc = its carrier circle
      startAngle: entity.startAngle,
      endAngle: entity.endAngle,
    };
  }
  return null;
}

function pointOnSegment(p: IntPoint, a: IntPoint, b: IntPoint): boolean {
  const len2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
  // Codex P2, round 12: `len2` is SQUARED-length but was compared against
  // the LINEAR-scale `GEO_EPS` — the degenerate-segment threshold this
  // implies is `sqrt(GEO_EPS) ≈ 3.16e-5`, not `GEO_EPS` itself, so a
  // genuinely short (down to ~31.6 micro-units) but still meaningful
  // boundary segment was wrongly folded into the "treat as a single point"
  // fallback, discarding the real segment direction/extent entirely. Same
  // squared-vs-linear class of bug already fixed elsewhere in this module
  // (`nearestPolylineParamCore`, round 8) — `GEO_EPS * GEO_EPS` is the
  // correctly-dimensioned squared-length floor, matching a genuine
  // linear-scale threshold of `GEO_EPS`.
  // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT
  // claimed as an equivalence proof) for THREE of this line's own mutants
  // specifically: the `< 1e-6` -> `<= 1e-6` EqualityOperator (the OTHER
  // direction — forcing the whole return `true`, and the `p.x + a.x`/
  // `p.y + a.y` ArithmeticOperator sign-flips — ARE independently killed;
  // see this file's own dedicated degenerate-Line-target-vs-Circle-edge
  // test), the `< 1e-6` -> `>= 1e-6` full-inversion, and (on the FOLLOWING
  // line's own range check) the `t >= -1e-6` -> `t > -1e-6` and `t <= 1 +
  // 1e-6` -> `t < 1 + 1e-6` EqualityOperator boundary pairs: each of these
  // requires either a computed distance/parameter landing EXACTLY (bit-for-
  // bit) on its own `1e-6` tolerance boundary, or a construction that
  // genuinely REJECTS under correct code (to discriminate the "always
  // admit" direction) — attempted via a near-zero-but-nonzero degenerate
  // segment paired with a boundary circle NOT passing through the
  // degenerate point, but `lineCircleIntersection`'s OWN `discriminant <
  // -1e-10` early-reject tolerance (many orders of magnitude coarser than
  // this function's own segment-length floor) swamps the astronomically
  // tiny discriminant this construction produces, snapping EVERY candidate
  // circle position to the SAME artifact hit at the segment's own start
  // point regardless of the circle's real position — making a genuine
  // "should reject" fixture at this precision scale unconstructible through
  // the public `entityIntersectionPoints` pipeline. Same class of exact-
  // double-boundary/degenerate-precision difficulty this file's `discTol`/
  // `half`/`stretchedEndPoint` comparisons already document.
  if (len2 < GEO_EPS * GEO_EPS) return Math.hypot(p.x - a.x, p.y - a.y) < 1e-6;
  const t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / len2;
  return t >= -1e-6 && t <= 1 + 1e-6;
}
